// ABOUTME: Renders the recruiter WhatsApp inbox with conversations, filters, and chat actions.
// ABOUTME: Connects inbox UI state to WhatsApp conversation APIs and realtime updates.
import React from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    makeStyles,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AddIcon from "@material-ui/icons/Add";
import AddCommentIcon from "@material-ui/icons/AddComment";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckIcon from "@material-ui/icons/Check";
import ChatIcon from "@material-ui/icons/Chat";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import EditIcon from "@material-ui/icons/Edit";
import FilterListIcon from "@material-ui/icons/FilterList";
import LabelIcon from "@material-ui/icons/Label";
import RefreshIcon from "@material-ui/icons/Refresh";
import ReplyIcon from "@material-ui/icons/Reply";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { apiDirectCall } from "customHooks/useApi";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { useAuthState } from "_ReactContext/AuthContext";
import { useWhatsappUnread } from "_ReactContext/WhatsappUnreadContext";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    buildWhatsappContactEpisodeActionRequest,
    createWhatsappActionRequestTracker,
    getWhatsappContactEpisodeActionContext,
    getWhatsappContactEpisodeActionEligibility,
    getWhatsappContactEpisodeActionContextForMode,
    getWhatsappContactEpisodeActionContexts,
    isWhatsappReplyTargetInActionContext,
    resolveWhatsappReplyToMessageId,
    resolveWhatsappContactEpisodeActionContextSelection,
    WhatsappContactEpisodeActionContext
} from "functions/whatsappContactEpisode";
import ApplicantViewModalContainer from "Pages/Applicant/ApplicantView/ApplicantViewModalContainer";
import Page from "Pages/_Layouts/MainInternalPage";
import WhatsappTemplateSendDialog from "Pages/WhatsappTemplateSendDialog";
import WhatsappInboxFilterDialog from "./WhatsappInboxFilterDialog";
import WhatsappContactEpisodePending from "./WhatsappContactEpisodePending";
import WhatsappInboxScopeTabs from "./WhatsappInboxScopeTabs";
import WhatsappResolvedContactEpisodeControls from "./WhatsappResolvedContactEpisodeControls";
import WhatsappInboxTimelineContinuation from "./WhatsappInboxTimelineContinuation";
import {
    buildWhatsappInboxFilterRequest,
    buildWhatsappInboxTimelineRequest,
    canOpenWhatsappMedia,
    canSendWhatsappSessionMessage,
    formatWhatsappInboxDate,
    formatWhatsappReactionText,
    getConversationTags,
    getDisplayText,
    getFirstDisplayText,
    getJobLabel,
    getJobTitle,
    getWhatsappConversationBooleanState,
    getWhatsappInboxActionConversationData,
    getWhatsappInboxActionTarget,
    getWhatsappInboxConversationName,
    getWhatsappInboxConversationIdentity,
    getWhatsappInboxConversationKey,
    getWhatsappInboxPhoneDisplay,
    getWhatsappInboxProfileContactDisplay,
    getWhatsappInboxTimelineKey,
    getWhatsappMessageActionContext,
    groupWhatsappInboxConversations,
    markWhatsappInboxTimelineAsRead,
    mergeWhatsappInboxStoreOptions,
    mergeWhatsappInboxTimelinePages,
    moveTagsToFront,
    normalizeTagList,
    normalizeWhatsappInboxFilterOptions,
    normalizeWhatsappInboxLanguage,
    normalizeWhatsappContactEpisodeMode,
    reconcileWhatsappInboxDraftFilters,
    resolveWhatsappInboxErrorMessage,
    shouldRefreshWhatsappInboxFilterOptions,
    shouldShowWhatsappContactEpisodeInbox,
    shouldIgnoreWhatsappConversationSelection,
    applyWhatsappInboxResponseIfCurrent,
    isWhatsappInboxRequestScopeCurrent,
    WhatsappInboxAppliedFilters,
    WhatsappInboxFilterOption,
    shouldClearWhatsappConversationSelection
} from "./helpers";

const moduleName = "WHATSAPPINBOX";

const useStyles = makeStyles((theme) => ({
    page: {
        height: "calc(100vh - 88px)",
        padding: theme.spacing(2),
        backgroundColor: "#f4f6f8",
        [theme.breakpoints.down("sm")]: {
            height: "auto",
            minHeight: "calc(100vh - 88px)",
            overflow: "auto"
        }
    },
    shell: {
        height: "100%",
        overflow: "hidden",
        borderRadius: 4,
        [theme.breakpoints.down("sm")]: {
            height: "auto",
            overflow: "visible"
        }
    },
    inboxLayout: {
        height: "100%",
        display: "flex",
        minWidth: 0,
        [theme.breakpoints.down("sm")]: {
            height: "auto",
            flexDirection: "column"
        }
    },
    listPane: {
        flex: "0 0 398px",
        width: 398,
        minWidth: 360,
        maxWidth: 430,
        height: "100%",
        borderRight: "1px solid #e0e0e0",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("md")]: {
            flexBasis: 360,
            width: 360,
            minWidth: 330
        },
        [theme.breakpoints.down("sm")]: {
            flex: "0 0 auto",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            height: 420,
            borderRight: 0,
            borderBottom: "1px solid #e0e0e0"
        }
    },
    threadPane: {
        flex: "1 1 0",
        minWidth: 0,
        height: "100%",
        backgroundColor: "#eef3f1",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("sm")]: {
            height: 620
        }
    },
    sidePane: {
        flex: "0 0 356px",
        width: 356,
        minWidth: 320,
        maxWidth: 380,
        height: "100%",
        borderLeft: "1px solid #e0e0e0",
        backgroundColor: "#fbfcfd",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("md")]: {
            flexBasis: 330,
            width: 330,
            minWidth: 300
        },
        [theme.breakpoints.down("sm")]: {
            flex: "0 0 auto",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            height: "auto",
            borderLeft: 0,
            borderTop: "1px solid #e0e0e0"
        }
    },
    header: {
        padding: theme.spacing(1.5),
        minHeight: 68,
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e0e0e0"
    },
    inboxToolbar: {
        padding: theme.spacing(1, 1.5),
        backgroundColor: "#f8faf9",
        borderBottom: "1px solid #e0e0e0"
    },
    tagRail: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        overflowX: "auto",
        paddingBottom: theme.spacing(0.75),
        whiteSpace: "nowrap"
    },
    filterSummary: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1)
    },
    scrollArea: {
        flex: 1,
        overflow: "auto"
    },
    conversationItem: {
        cursor: "pointer",
        borderBottom: "1px solid #edf1ef",
        alignItems: "flex-start",
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25)
    },
    selectedConversation: {
        backgroundColor: "#e7f3ef"
    },
    conversationAvatar: {
        width: 42,
        height: 42,
        backgroundColor: "#d7ede5",
        color: "#16352b",
        fontWeight: 700
    },
    conversationPrimary: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1)
    },
    conversationName: {
        flex: 1,
        minWidth: 0,
        fontWeight: 600
    },
    conversationPhone: {
        flexShrink: 0,
        fontSize: 12,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap"
    },
    conversationTime: {
        flexShrink: 0,
        fontSize: 11,
        color: theme.palette.text.secondary
    },
    conversationPreview: {
        display: "block",
        maxWidth: "100%",
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
    miniTagRow: {
        display: "flex",
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
        flexWrap: "wrap"
    },
    miniTag: {
        height: 20,
        maxWidth: 112,
        "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis"
        }
    },
    unreadChip: {
        height: 22,
        minWidth: 22,
        marginLeft: theme.spacing(0.5)
    },
    messages: {
        flex: 1,
        overflow: "auto",
        padding: theme.spacing(2)
    },
    messageRow: {
        display: "flex",
        marginBottom: theme.spacing(1)
    },
    outgoingRow: {
        justifyContent: "flex-end"
    },
    bubble: {
        maxWidth: "72%",
        padding: theme.spacing(1),
        borderRadius: 6,
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.12)"
    },
    messageText: {
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere"
    },
    outgoingBubble: {
        backgroundColor: "#dcf8c6"
    },
    noteBubble: {
        backgroundColor: "#fff4ce",
        border: "1px solid #f3d27a"
    },
    quotePreview: {
        display: "block",
        marginBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(1),
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        overflowWrap: "anywhere"
    },
    composer: {
        padding: theme.spacing(1.5),
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e0e0e0"
    },
    muted: {
        color: theme.palette.text.secondary
    },
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1)
    },
    sideContent: {
        padding: theme.spacing(2),
        overflow: "auto"
    },
    profileHero: {
        paddingBottom: theme.spacing(2),
        borderBottom: "1px solid #e7ece9"
    },
    profileHeroTop: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5)
    },
    profileAvatar: {
        width: 52,
        height: 52,
        backgroundColor: "#d7ede5",
        color: "#16352b",
        fontWeight: 700
    },
    profileName: {
        fontWeight: 700,
        color: "#1d1430"
    },
    profileEmail: {
        color: theme.palette.text.secondary,
        wordBreak: "break-word"
    },
    profileSection: {
        padding: theme.spacing(2, 0),
        borderBottom: "1px solid #e7ece9"
    },
    profileSectionHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    profileSectionTitle: {
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0,
        color: "#5d6670"
    },
    profileDetails: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: theme.spacing(1)
    },
    profileDetailItem: {
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(1.5),
        fontSize: 13
    },
    profileDetailLabel: {
        color: theme.palette.text.secondary
    },
    profileDetailValue: {
        color: "#1d1430",
        textAlign: "right",
        overflowWrap: "anywhere"
    },
    profileTagRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.75)
    },
    editableTag: {
        maxWidth: "100%",
        "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis"
        }
    },
    editableTagGroup: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.25),
        maxWidth: "100%"
    },
    tagEditor: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1)
    },
    jobList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1)
    },
    jobItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        borderRadius: 6,
        backgroundColor: "#ffffff",
        border: "1px solid #e7ece9"
    },
    jobTitle: {
        minWidth: 0,
        fontWeight: 600,
        color: "#1d1430"
    },
    jobCode: {
        flexShrink: 0,
        height: 22
    },
    contactResults: {
        marginTop: theme.spacing(1),
        maxHeight: 320,
        overflow: "auto"
    }
}));

const buildInboxWebsocketUrl = (
    apiBaseUrl: string,
    params: { ticket: string; companyId: number }
) => {
    const trimmedBaseUrl = apiBaseUrl.replace(/\/$/, "");
    const websocketBaseUrl = trimmedBaseUrl.replace(/^http/i, "ws");
    const searchParams = new URLSearchParams({
        ticket: params.ticket,
        companyId: String(params.companyId)
    });

    return `${websocketBaseUrl}/whatsapp/inboxws?${searchParams.toString()}`;
};

const useWhatsappInboxRealtimeRevision = (params: {
    companyId: number;
    enabled: boolean;
}) => {
    const { companyId, enabled } = params;
    const [revision, setRevision] = React.useState(0);

    React.useEffect(() => {
        if (!companyId || !enabled || typeof WebSocket === "undefined") {
            return;
        }
        let disposed = false;
        let socket: WebSocket | null = null;
        let reconnectTimer: number | null = null;
        const connect = async () => {
            try {
                const ticketResponse = await apiDirectCall(
                    "/whatsapp/inboxticket",
                    "post",
                    { companyId }
                );
                const ticket = getWhatsappApiResponseData(ticketResponse)
                    .ticket;
                if (!ticket || disposed) {
                    return;
                }
                socket = new WebSocket(
                    buildInboxWebsocketUrl(import.meta.env.VITE_API_ADDRESS, {
                        ticket,
                        companyId
                    })
                );
                socket.onmessage = () => {
                    setRevision((current) => current + 1);
                };
                socket.onclose = () => {
                    if (!disposed) {
                        reconnectTimer = window.setTimeout(connect, 3000);
                    }
                };
            } catch {
                if (!disposed) {
                    reconnectTimer = window.setTimeout(connect, 5000);
                }
            }
        };
        connect();
        return () => {
            disposed = true;
            if (reconnectTimer) {
                window.clearTimeout(reconnectTimer);
            }
            socket?.close();
        };
    }, [companyId, enabled]);

    return revision;
};

const getErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.message ||
    fallback;

const getItemText = (
    item: Compleo.IObject,
    labels: { reactedWith?: string } = {}
) =>
    formatWhatsappReactionText(item, labels) ||
    item.noteText ||
    item.templateRenderedText ||
    item.textContent ||
    item.caption ||
    item.originalFileName ||
    item.templateName ||
    item.type ||
    "";

const isWhatsappMediaItem = (item: Compleo.IObject) =>
    ["document", "image"].includes(String(item.type || ""));

const isGeneratedWhatsappMediaName = (name: string) =>
    /^whatsapp_wamid/i.test(name);

const getWhatsappMediaLabel = (
    item: Compleo.IObject,
    t: (key: string) => string
) => {
    const originalFileName = String(item.originalFileName || "").trim();
    const caption = String(item.caption || "").trim();
    const name =
        item.type === "image" && isGeneratedWhatsappMediaName(originalFileName)
            ? caption
            : originalFileName || caption;

    if (name) {
        return name;
    }

    return item.type === "image" ? t("image") : t("file");
};

const TAG_COLORS = [
    { background: "#e2f3ec", color: "#0e5a44" },
    { background: "#eef0ff", color: "#293b8f" },
    { background: "#fff2d7", color: "#72510b" },
    { background: "#fde7ed", color: "#8a2342" },
    { background: "#e6f1fb", color: "#174c78" },
    { background: "#ece7f7", color: "#4d327b" },
    { background: "#edf4df", color: "#425f14" },
    { background: "#f1ece5", color: "#60462a" }
];

const getTagColor = (tag: string) => {
    const hash = tag
        .split("")
        .reduce((total, char) => total + char.charCodeAt(0), 0);

    return TAG_COLORS[hash % TAG_COLORS.length];
};

const getInitials = (value?: string) => {
    const parts = String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (parts.length === 0) {
        return "?";
    }

    return parts
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
};

const applyOnEnter = (
    event: React.KeyboardEvent<HTMLDivElement>,
    callback: () => void
) => {
    if (event.key === "Enter") {
        callback();
    }
};

const WhatsappInboxContent = (props: {
    embedded?: boolean;
    realtimeRevision?: number;
}) => {
    const { embedded = false, realtimeRevision = 0 } = props;
    const classes = useStyles();
    const showMessage = useShowMessage();
    const showMessageRef = React.useRef(showMessage);
    const [t, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const language = normalizeWhatsappInboxLanguage(
        i18n.languages?.[0] || i18n.language
    );
    const { company } = useAuthState();
    const companyId = Number(company?.id || company?.companyId);
    const whatsappEnabled = company?.whatsappEnabled === true;
    const whatsappContactEpisodeMode = normalizeWhatsappContactEpisodeMode(
        company?.whatsappContactEpisodeMode
    );
    const embeddedPageStyle = embedded
        ? { height: "100%", padding: 0 }
        : undefined;
    const [conversations, setConversations] = React.useState<Compleo.IObject[]>(
        []
    );
    const [knownTags, setKnownTags] = React.useState<string[]>([]);
    const [selectedApplicantId, setSelectedApplicantId] = React.useState("");
    const [selectedSegmentId, setSelectedSegmentId] = React.useState("");
    const [conversationData, setConversationData] = React.useState<
        Compleo.IObject
    >({});
    const [search, setSearch] = React.useState("");
    const [selectedJobFilters, setSelectedJobFilters] = React.useState<
        Compleo.IObject[]
    >([]);
    const [draftJobFilters, setDraftJobFilters] = React.useState<
        Compleo.IObject[]
    >([]);
    const [selectedStoreFilters, setSelectedStoreFilters] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [draftStoreFilters, setDraftStoreFilters] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [selectedRegionFilters, setSelectedRegionFilters] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [draftRegionFilters, setDraftRegionFilters] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [
        selectedRecruiterFilters,
        setSelectedRecruiterFilters
    ] = React.useState<WhatsappInboxFilterOption[]>([]);
    const [draftRecruiterFilters, setDraftRecruiterFilters] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [
        draftStartedByCurrentUser,
        setDraftStartedByCurrentUser
    ] = React.useState(false);
    const [appliedFilters, setAppliedFilters] = React.useState<
        WhatsappInboxAppliedFilters
    >({
        search: "",
        jobIds: [],
        jobCodes: [],
        storeIds: [],
        regionIds: [],
        mainRecruiterEmails: [],
        startedByCurrentUser: false,
        tags: []
    });
    const [nextCursor, setNextCursor] = React.useState("");
    const [messageSearch, setMessageSearch] = React.useState("");
    const [messageSearchResults, setMessageSearchResults] = React.useState<
        Compleo.IObject[]
    >([]);
    const [messageSearchLoading, setMessageSearchLoading] = React.useState(
        false
    );
    const [messageSearchExecuted, setMessageSearchExecuted] = React.useState(
        false
    );
    const [composerText, setComposerText] = React.useState("");
    const [composerFile, setComposerFile] = React.useState<File | null>(null);
    const [noteText, setNoteText] = React.useState("");
    const [tagEditorMode, setTagEditorMode] = React.useState<
        "closed" | "add" | "edit"
    >("closed");
    const [tagEditorValue, setTagEditorValue] = React.useState("");
    const [tagEditorOriginal, setTagEditorOriginal] = React.useState("");
    const [tagSaving, setTagSaving] = React.useState(false);
    const [replyTo, setReplyTo] = React.useState<Compleo.IObject | null>(null);
    const [
        selectedContactEpisodeActionContext,
        setSelectedContactEpisodeActionContext
    ] = React.useState<WhatsappContactEpisodeActionContext | undefined>();
    const [templateDialogOpen, setTemplateDialogOpen] = React.useState(false);
    const [loadingList, setLoadingList] = React.useState(false);
    const [loadingConversation, setLoadingConversation] = React.useState(false);
    const [loadingMoreContexts, setLoadingMoreContexts] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [contactDialogOpen, setContactDialogOpen] = React.useState(false);
    const [contactSearch, setContactSearch] = React.useState("");
    const [contactResults, setContactResults] = React.useState<
        Compleo.IObject[]
    >([]);
    const [contactLoading, setContactLoading] = React.useState(false);
    const [contactSearchExecuted, setContactSearchExecuted] = React.useState(
        false
    );
    const [jobFilterOpen, setJobFilterOpen] = React.useState(false);
    const [jobSearch, setJobSearch] = React.useState("");
    const [jobResults, setJobResults] = React.useState<Compleo.IObject[]>([]);
    const [jobLoading, setJobLoading] = React.useState(false);
    const [jobSearchExecuted, setJobSearchExecuted] = React.useState(false);
    const [filterOptionsLoading, setFilterOptionsLoading] = React.useState(
        false
    );
    const [storeOptions, setStoreOptions] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [regionOptions, setRegionOptions] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [recruiterOptions, setRecruiterOptions] = React.useState<
        WhatsappInboxFilterOption[]
    >([]);
    const [profileOpen, setProfileOpen] = React.useState(false);
    const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
    const tRef = React.useRef(t);
    const languageRef = React.useRef(language);
    const composerInputRef = React.useRef<
        HTMLInputElement | HTMLTextAreaElement | null
    >(null);
    const composerFileInputRef = React.useRef<HTMLInputElement | null>(null);
    const conversationsRequestRef = React.useRef(0);
    const conversationRequestRef = React.useRef(0);
    const messageSearchRequestRef = React.useRef(0);
    const contactSearchRequestRef = React.useRef(0);
    const jobSearchRequestRef = React.useRef(0);
    const filterOptionsRequestRef = React.useRef(0);
    const sendRequestRef = React.useRef(0);
    const tagSaveRequestRef = React.useRef(0);
    const actionRequestTrackerRef = React.useRef(
        createWhatsappActionRequestTracker()
    );
    const companyIdRef = React.useRef(companyId);
    const selectedApplicantIdRef = React.useRef(selectedApplicantId);
    const selectedSegmentIdRef = React.useRef(selectedSegmentId);
    const selectedConversationKey = getWhatsappInboxTimelineKey({
        applicantId: selectedApplicantId,
        segmentId: selectedApplicantId ? "" : selectedSegmentId
    });
    const selectedConversationKeyRef = React.useRef(selectedConversationKey);
    const selectedConversationUsesInboxFiltersRef = React.useRef(true);
    const skipNextConversationRefreshRef = React.useRef(false);
    const refreshConversationsRef = React.useRef<() => void>(() => undefined);
    const refreshConversationRef = React.useRef<
        (applicantId?: string, segmentId?: string) => void
    >(() => undefined);
    companyIdRef.current = companyId;
    selectedApplicantIdRef.current = selectedApplicantId;
    selectedSegmentIdRef.current = selectedSegmentId;
    selectedConversationKeyRef.current = selectedConversationKey;

    const getCurrentInboxRequestScope = React.useCallback(
        () => ({
            companyId: companyIdRef.current,
            applicantId: selectedApplicantIdRef.current,
            segmentId: selectedSegmentIdRef.current,
            conversationKey: selectedConversationKeyRef.current
        }),
        []
    );

    const resetConversationState = React.useCallback(() => {
        setConversationData({});
        setReplyTo(null);
        setSelectedContactEpisodeActionContext(undefined);
        setComposerText("");
        setComposerFile(null);
        setNoteText("");
        setTagEditorMode("closed");
        setTagEditorValue("");
        setTagEditorOriginal("");
        setTagSaving(false);
        setSending(false);
        setMessageSearch("");
        setMessageSearchResults([]);
        setMessageSearchExecuted(false);
        setMessageSearchLoading(false);
        setTemplateDialogOpen(false);
        setProfileOpen(false);
        setLoadingConversation(false);
        setLoadingMoreContexts(false);
    }, []);

    const clearSelectedApplicant = React.useCallback(() => {
        conversationRequestRef.current += 1;
        selectedApplicantIdRef.current = "";
        selectedSegmentIdRef.current = "";
        selectedConversationKeyRef.current = "";
        selectedConversationUsesInboxFiltersRef.current = true;
        messageSearchRequestRef.current += 1;
        sendRequestRef.current += 1;
        tagSaveRequestRef.current += 1;
        setSelectedApplicantId("");
        setSelectedSegmentId("");
        resetConversationState();
    }, [resetConversationState]);

    React.useEffect(() => {
        showMessageRef.current = showMessage;
    }, [showMessage]);

    React.useEffect(() => {
        tRef.current = t;
    }, [t]);

    React.useEffect(() => {
        languageRef.current = language;
    }, [language]);

    const notify = React.useCallback(
        (
            message: Parameters<typeof showMessage>[0],
            variant?: Parameters<typeof showMessage>[1],
            timeout?: Parameters<typeof showMessage>[2]
        ) => {
            showMessageRef.current(message, variant, timeout);
        },
        []
    );

    React.useEffect(() => {
        selectedApplicantIdRef.current = selectedApplicantId;
    }, [selectedApplicantId]);

    React.useEffect(() => {
        selectedSegmentIdRef.current = selectedSegmentId;
        selectedConversationKeyRef.current = selectedConversationKey;
    }, [selectedConversationKey, selectedSegmentId]);

    React.useEffect(() => {
        companyIdRef.current = companyId;
    }, [companyId]);

    React.useEffect(() => {
        skipNextConversationRefreshRef.current = true;
        conversationsRequestRef.current += 1;
        conversationRequestRef.current += 1;
        contactSearchRequestRef.current += 1;
        jobSearchRequestRef.current += 1;
        filterOptionsRequestRef.current += 1;
        messageSearchRequestRef.current += 1;
        sendRequestRef.current += 1;
        tagSaveRequestRef.current += 1;
        setKnownTags([]);
        setConversations([]);
        setSelectedApplicantId("");
        setSelectedSegmentId("");
        selectedApplicantIdRef.current = "";
        selectedSegmentIdRef.current = "";
        selectedConversationKeyRef.current = "";
        selectedConversationUsesInboxFiltersRef.current = true;
        setConversationData({});
        setSearch("");
        setSelectedJobFilters([]);
        setDraftJobFilters([]);
        setSelectedStoreFilters([]);
        setDraftStoreFilters([]);
        setSelectedRegionFilters([]);
        setDraftRegionFilters([]);
        setSelectedRecruiterFilters([]);
        setDraftRecruiterFilters([]);
        setDraftStartedByCurrentUser(false);
        setAppliedFilters({
            search: "",
            jobIds: [],
            jobCodes: [],
            storeIds: [],
            regionIds: [],
            mainRecruiterEmails: [],
            startedByCurrentUser: false,
            tags: []
        });
        setNextCursor("");
        setContactSearch("");
        setContactResults([]);
        setContactSearchExecuted(false);
        setContactLoading(false);
        setContactDialogOpen(false);
        setJobSearch("");
        setJobResults([]);
        setJobSearchExecuted(false);
        setJobLoading(false);
        setJobFilterOpen(false);
        setFilterOptionsLoading(false);
        setStoreOptions([]);
        setRegionOptions([]);
        setRecruiterOptions([]);
        setMessageSearchResults([]);
        setMessageSearch("");
        setMessageSearchExecuted(false);
        setMessageSearchLoading(false);
        setReplyTo(null);
        setTemplateDialogOpen(false);
        setComposerText("");
        setComposerFile(null);
        setNoteText("");
        setTagEditorMode("closed");
        setTagEditorValue("");
        setTagEditorOriginal("");
        setTagSaving(false);
        setSending(false);
        setProfileOpen(false);
        setLoadingConversation(false);
        setLoadingMoreContexts(false);
        setLoadingList(false);
    }, [companyId]);

    const refreshConversations = React.useCallback(
        async (cursor = "") => {
            if (!companyId || !whatsappEnabled) {
                return;
            }

            const requestId = conversationsRequestRef.current + 1;
            conversationsRequestRef.current = requestId;
            setLoadingList(true);
            try {
                const result = await apiDirectCall(
                    "/whatsapp/conversations",
                    "post",
                    {
                        companyId,
                        ...buildWhatsappInboxFilterRequest(appliedFilters),
                        cursor,
                        limit: 50,
                        contactEpisodeCapability: "CONTACT_EPISODE_V1"
                    }
                );
                if (conversationsRequestRef.current !== requestId) {
                    return;
                }
                const data = getWhatsappApiResponseData(result);
                const loadedConversations = data.conversations || [];
                setConversations((current) =>
                    groupWhatsappInboxConversations(
                        cursor
                            ? [...current, ...loadedConversations]
                            : loadedConversations
                    )
                );
                if (
                    !cursor &&
                    selectedConversationUsesInboxFiltersRef.current &&
                    shouldClearWhatsappConversationSelection(
                        selectedConversationKeyRef.current,
                        groupWhatsappInboxConversations(loadedConversations),
                        appliedFilters
                    )
                ) {
                    clearSelectedApplicant();
                }
                setKnownTags((current) => {
                    const next = new Set(current);
                    loadedConversations.forEach(
                        (conversation: Compleo.IObject) => {
                            getConversationTags(
                                conversation,
                                languageRef.current
                            ).forEach((tag) => next.add(tag));
                        }
                    );
                    return next.size === current.length
                        ? current
                        : Array.from(next);
                });
                setNextCursor(data.nextCursor || "");
            } catch (error) {
                if (conversationsRequestRef.current === requestId) {
                    notify(
                        getErrorMessage(
                            error,
                            tRef.current("loadConversationsError")
                        ),
                        "error"
                    );
                }
            } finally {
                if (conversationsRequestRef.current === requestId) {
                    setLoadingList(false);
                }
            }
        },
        [
            appliedFilters,
            clearSelectedApplicant,
            companyId,
            notify,
            whatsappEnabled
        ]
    );

    const refreshConversation = React.useCallback(
        async (
            applicantId = selectedApplicantIdRef.current,
            segmentId = selectedSegmentIdRef.current
        ) => {
            const conversationKey = getWhatsappInboxTimelineKey({
                applicantId,
                segmentId: applicantId ? "" : segmentId
            });
            if (!companyId || !conversationKey) {
                return;
            }

            const requestId = conversationRequestRef.current + 1;
            conversationRequestRef.current = requestId;
            const requestScope = {
                companyId,
                applicantId,
                segmentId,
                conversationKey
            };
            setLoadingConversation(true);
            setLoadingMoreContexts(false);
            try {
                const result = await apiDirectCall(
                    "/whatsapp/conversation",
                    "post",
                    {
                        companyId,
                        ...buildWhatsappInboxTimelineRequest(
                            applicantId,
                            segmentId
                        ),
                        limit: 200,
                        includeInternalNotes: true,
                        contactEpisodeCapability: "CONTACT_EPISODE_V1"
                    }
                );
                if (conversationRequestRef.current !== requestId) {
                    return;
                }
                applyWhatsappInboxResponseIfCurrent(
                    requestScope,
                    getCurrentInboxRequestScope(),
                    () => {
                        setConversationData(getWhatsappApiResponseData(result));
                    }
                );
            } catch (error) {
                if (
                    conversationRequestRef.current === requestId &&
                    isWhatsappInboxRequestScopeCurrent(
                        requestScope,
                        getCurrentInboxRequestScope()
                    )
                ) {
                    notify(
                        getErrorMessage(
                            error,
                            tRef.current("loadConversationError")
                        ),
                        "error"
                    );
                }
            } finally {
                if (
                    conversationRequestRef.current === requestId &&
                    isWhatsappInboxRequestScopeCurrent(
                        requestScope,
                        getCurrentInboxRequestScope()
                    )
                ) {
                    setLoadingConversation(false);
                }
            }
        },
        [companyId, getCurrentInboxRequestScope, notify]
    );

    const loadMoreTimelineContexts = React.useCallback(async () => {
        const applicantId = selectedApplicantIdRef.current;
        const conversationKey = selectedConversationKeyRef.current;
        const contextCursor = String(
            conversationData.nextContextCursor || ""
        );
        if (
            !companyId ||
            !applicantId ||
            !conversationKey ||
            !contextCursor ||
            loadingConversation ||
            loadingMoreContexts
        ) {
            return;
        }

        const requestId = conversationRequestRef.current + 1;
        conversationRequestRef.current = requestId;
        const requestScope = {
            companyId,
            applicantId,
            segmentId: selectedSegmentIdRef.current,
            conversationKey
        };
        setLoadingMoreContexts(true);
        try {
            const result = await apiDirectCall(
                "/whatsapp/conversation",
                "post",
                {
                    companyId,
                    ...buildWhatsappInboxTimelineRequest(applicantId, ""),
                    contextCursor,
                    limit: 200,
                    includeInternalNotes: true,
                    contactEpisodeCapability: "CONTACT_EPISODE_V1"
                }
            );
            if (
                conversationRequestRef.current !== requestId ||
                !isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                return;
            }

            const nextPage = getWhatsappApiResponseData(result);
            setConversationData((current) =>
                String(current.nextContextCursor || "") === contextCursor
                    ? mergeWhatsappInboxTimelinePages(current, nextPage)
                    : current
            );
        } catch (error) {
            if (
                conversationRequestRef.current === requestId &&
                isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                notify(
                    getErrorMessage(
                        error,
                        tRef.current("loadMoreContextsError")
                    ),
                    "error"
                );
            }
        } finally {
            if (
                conversationRequestRef.current === requestId &&
                isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                setLoadingMoreContexts(false);
            }
        }
    }, [
        companyId,
        conversationData.nextContextCursor,
        getCurrentInboxRequestScope,
        loadingConversation,
        loadingMoreContexts,
        notify
    ]);

    React.useEffect(() => {
        if (skipNextConversationRefreshRef.current) {
            skipNextConversationRefreshRef.current = false;
            return;
        }

        refreshConversations();
    }, [refreshConversations]);

    React.useEffect(() => {
        refreshConversationsRef.current = () => refreshConversations();
    }, [refreshConversations]);

    React.useEffect(() => {
        refreshConversationRef.current = refreshConversation;
    }, [refreshConversation]);

    React.useEffect(() => {
        if (selectedConversationKey) {
            refreshConversation(selectedApplicantId, selectedSegmentId);
        } else {
            conversationRequestRef.current += 1;
            setConversationData((current) =>
                Object.keys(current).length > 0 ? {} : current
            );
        }
    }, [
        refreshConversation,
        selectedApplicantId,
        selectedConversationKey,
        selectedSegmentId
    ]);

    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [conversationData.items?.length, selectedConversationKey]);

    React.useEffect(() => {
        if (realtimeRevision < 1) {
            return;
        }
        refreshConversationsRef.current();
        if (selectedConversationKeyRef.current) {
            refreshConversationRef.current(
                selectedApplicantIdRef.current,
                selectedSegmentIdRef.current
            );
        }
    }, [realtimeRevision]);

    const selectedConversation = React.useMemo(
        () =>
            conversations.find(
                (item) =>
                    getWhatsappInboxTimelineKey(item) ===
                    selectedConversationKey
            ) || {},
        [conversations, selectedConversationKey]
    );
    const actionContext = React.useMemo(
        () =>
            selectedApplicantId
                ? { scope: "JOB" as const }
                : selectedSegmentId
                ? {
                      scope: "PRE_REGISTRATION" as const,
                      segmentId: selectedSegmentId
                  }
                : null,
        [selectedApplicantId, selectedSegmentId]
    );
    React.useEffect(() => {
        const nextTags = new Set<string>();

        conversations.forEach((conversation) => {
            getConversationTags(conversation, language).forEach((tag) =>
                nextTags.add(tag)
            );
        });

        const nextKnownTags = Array.from(nextTags);
        setKnownTags((current) =>
            current.length === nextKnownTags.length &&
            current.every((tag, index) => tag === nextKnownTags[index])
                ? current
                : nextKnownTags
        );
    }, [conversations, language]);

    const availableTags = React.useMemo(() => {
        const tags = new Set<string>(appliedFilters.tags);
        knownTags.forEach((tag) => tags.add(tag));

        return Array.from(tags).filter(Boolean);
    }, [appliedFilters.tags, knownTags]);
    const candidate = conversationData.candidate || selectedConversation || {};
    const actionConversationData = getWhatsappInboxActionConversationData(
        conversationData,
        selectedApplicantId
    );
    const recipientPhone = getWhatsappInboxPhoneDisplay(
        conversationData.channel?.recipientPhone,
        language
    );
    const candidateTags = React.useMemo(
        () =>
            Array.isArray(candidate.tags)
                ? normalizeTagList(candidate.tags as any[], language)
                : [],
        [candidate.tags, language]
    );
    const items = (conversationData.items || []) as Compleo.IObject[];
    const contactEpisodeActionContexts = React.useMemo(
        () =>
            getWhatsappContactEpisodeActionContexts(
                conversationData.contactEpisodeActionContexts || []
            ),
        [conversationData.contactEpisodeActionContexts]
    );
    const refreshedContactEpisodeActionContext =
        resolveWhatsappContactEpisodeActionContextSelection(
            contactEpisodeActionContexts,
            selectedContactEpisodeActionContext
        );
    const activeContactEpisodeActionContext =
        getWhatsappContactEpisodeActionContextForMode(
            whatsappContactEpisodeMode,
            refreshedContactEpisodeActionContext
        );
    React.useEffect(() => {
        if (
            replyTo &&
            resolveWhatsappReplyToMessageId(
                replyTo,
                activeContactEpisodeActionContext
            ) === undefined
        ) {
            setReplyTo(null);
        }
    }, [activeContactEpisodeActionContext, replyTo]);
    React.useEffect(() => {
        if (!selectedContactEpisodeActionContext) {
            return;
        }
        if (!refreshedContactEpisodeActionContext) {
            setSelectedContactEpisodeActionContext(undefined);
        }
    }, [
        refreshedContactEpisodeActionContext,
        selectedContactEpisodeActionContext
    ]);
    React.useEffect(() => {
        if (
            whatsappContactEpisodeMode !== "ACTIVE" &&
            selectedContactEpisodeActionContext
        ) {
            setSelectedContactEpisodeActionContext(undefined);
        }
    }, [selectedContactEpisodeActionContext, whatsappContactEpisodeMode]);
    const visibleItems = React.useMemo(() => {
        if (messageSearchExecuted && messageSearch.trim()) {
            return messageSearchResults;
        }

        return items;
    }, [items, messageSearch, messageSearchExecuted, messageSearchResults]);
    const itemsByMessageId = React.useMemo(() => {
        const nextItems = new Map<string, Compleo.IObject>();
        items.forEach((item) => {
            if (item.messageId) {
                nextItems.set(String(item.messageId), item);
            }
        });

        return nextItems;
    }, [items]);
    const baseOptedIn = getWhatsappConversationBooleanState(
        actionConversationData,
        {},
        "optedIn"
    );
    const hasConversationConsent = Object.prototype.hasOwnProperty.call(
        actionConversationData,
        selectedApplicantId ? "optedOut" : "consent"
    );
    const baseOptedOut = hasConversationConsent
        ? selectedApplicantId
            ? actionConversationData.optedOut === true
            : actionConversationData.consent?.optedOut === true
        : false;
    const baseHasWindow = getWhatsappConversationBooleanState(
        actionConversationData,
        {},
        "hasActiveCustomerServiceWindow"
    );
    const baseCanSendSession =
        selectedConversationKey &&
        canSendWhatsappSessionMessage(actionConversationData);
    const hasConversationTemplateEligibility = Object.prototype.hasOwnProperty.call(
        actionConversationData,
        "canSendTemplate"
    );
    const baseCanStartWithTemplate =
        selectedApplicantId &&
        !baseOptedOut &&
        (hasConversationTemplateEligibility
            ? actionConversationData.canSendTemplate === true
            : false);
    const actionEligibility = getWhatsappContactEpisodeActionEligibility(
        {
            optedIn: baseOptedIn,
            optedOut: baseOptedOut,
            marketingOptedOut:
                !selectedApplicantId &&
                actionConversationData.consent?.marketingOptedOut === true,
            phoneSuppressed:
                !selectedApplicantId &&
                actionConversationData.consent?.phoneSuppressed === true,
            manualOptInBlocked:
                !selectedApplicantId &&
                actionConversationData.consent?.manualOptInBlocked === true,
            hasActiveCustomerServiceWindow: baseHasWindow,
            canSendSessionMessage: Boolean(baseCanSendSession),
            canSendTemplate: Boolean(baseCanStartWithTemplate)
        },
        activeContactEpisodeActionContext
    );
    const optedIn = actionEligibility.optedIn;
    const optedOut = actionEligibility.optedOut;
    const hasWindow = actionEligibility.hasActiveCustomerServiceWindow;
    const canSendSession = actionEligibility.canSendSessionMessage;
    const canStartWithTemplate =
        selectedApplicantId && actionEligibility.canSendTemplate;
    const selectedCandidateJobOptions = React.useMemo(
        () =>
            ((candidate.jobs || []) as Compleo.IObject[])
                .map((job) => {
                    const jobId = getDisplayText(
                        job.jobId || job.JobId,
                        language
                    );
                    const title = getJobTitle(job, language) || jobId;

                    return {
                        jobId,
                        label:
                            jobId && title !== jobId
                                ? `${title} (${jobId})`
                                : title
                    };
                })
                .filter((job) => job.jobId),
        [candidate.jobs, language]
    );
    const hasAppliedConversationFilters = React.useMemo(
        () =>
            appliedFilters.jobIds.length > 0 ||
            appliedFilters.storeIds.length > 0 ||
            appliedFilters.regionIds.length > 0 ||
            appliedFilters.mainRecruiterEmails.length > 0 ||
            appliedFilters.startedByCurrentUser === true,
        [appliedFilters]
    );
    const applyConversationSearch = React.useCallback(
        (value = search) => {
            const text = value.trim();
            const nextSearch = text.length >= 4 ? text : "";
            conversationsRequestRef.current += 1;
            setAppliedFilters((current) =>
                current.search === nextSearch
                    ? current
                    : {
                          ...current,
                          search: nextSearch
                      }
            );
        },
        [search]
    );

    const focusComposerInput = React.useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.setTimeout(() => {
            composerInputRef.current?.focus();
        }, 0);
    }, []);

    React.useEffect(() => {
        const text = search.trim();
        if (text.length === 0) {
            if (appliedFilters.search) {
                applyConversationSearch("");
            }
            return;
        }

        if (text.length < 4) {
            if (appliedFilters.search) {
                applyConversationSearch("");
            }
            return;
        }

        if (appliedFilters.search === text) {
            return;
        }

        const timer = window.setTimeout(() => {
            applyConversationSearch(text);
        }, 300);

        return () => {
            window.clearTimeout(timer);
        };
    }, [appliedFilters.search, applyConversationSearch, search]);

    const toggleTagFilter = React.useCallback((tag: string) => {
        conversationsRequestRef.current += 1;
        setAppliedFilters((current) => {
            const selected = current.tags.includes(tag);
            return {
                ...current,
                tags: selected
                    ? current.tags.filter((item) => item !== tag)
                    : [...current.tags, tag]
            };
        });
    }, []);

    const clearTagFilters = React.useCallback(() => {
        conversationsRequestRef.current += 1;
        setAppliedFilters((current) => ({
            ...current,
            tags: []
        }));
    }, []);

    const loadFilterOptions = React.useCallback(async () => {
        if (
            !shouldRefreshWhatsappInboxFilterOptions({
                companyId,
                isLoading: filterOptionsLoading
            })
        ) {
            return;
        }

        const requestId = filterOptionsRequestRef.current + 1;
        filterOptionsRequestRef.current = requestId;
        setFilterOptionsLoading(true);

        try {
            const result = await apiDirectCall(
                "/whatsapp/filteroptions",
                "post",
                {
                    companyId,
                    limit: 500
                }
            );
            const data = normalizeWhatsappInboxFilterOptions(
                getWhatsappApiResponseData(result)
            );
            if (filterOptionsRequestRef.current !== requestId) {
                return;
            }

            setStoreOptions(
                mergeWhatsappInboxStoreOptions(data.branches, data.stores)
            );
            setRegionOptions(data.regions);
            setRecruiterOptions(data.mainRecruiters);
        } catch (error) {
            if (filterOptionsRequestRef.current === requestId) {
                notify(
                    resolveWhatsappInboxErrorMessage(
                        error,
                        tRef.current,
                        tRef.current("loadFiltersError")
                    ),
                    "error"
                );
            }
        } finally {
            if (filterOptionsRequestRef.current === requestId) {
                setFilterOptionsLoading(false);
            }
        }
    }, [companyId, filterOptionsLoading, notify]);

    const openJobFilterDialog = React.useCallback(() => {
        setDraftJobFilters(selectedJobFilters);
        setDraftStoreFilters(selectedStoreFilters);
        setDraftRegionFilters(selectedRegionFilters);
        setDraftRecruiterFilters(selectedRecruiterFilters);
        setDraftStartedByCurrentUser(appliedFilters.startedByCurrentUser);
        setJobSearch("");
        setJobResults([]);
        setJobSearchExecuted(false);
        setJobLoading(false);
        setJobFilterOpen(true);
        void loadFilterOptions();
    }, [
        appliedFilters.startedByCurrentUser,
        loadFilterOptions,
        selectedJobFilters,
        selectedRegionFilters,
        selectedRecruiterFilters,
        selectedStoreFilters
    ]);

    const closeJobFilterDialog = React.useCallback(() => {
        jobSearchRequestRef.current += 1;
        setJobLoading(false);
        setJobFilterOpen(false);
    }, []);

    const applyJobFilters = React.useCallback(() => {
        const jobIds = draftJobFilters
            .map((job) => String(job.jobId || ""))
            .filter(Boolean);
        const storeIds = draftStoreFilters
            .map((store) => String(store.value || ""))
            .filter(Boolean);
        const regionIds = draftRegionFilters
            .map((region) => String(region.value || ""))
            .filter(Boolean);
        const mainRecruiterEmails = draftRecruiterFilters
            .map((recruiter) =>
                String(recruiter.email || recruiter.value || "")
            )
            .filter(Boolean);
        conversationsRequestRef.current += 1;
        setSelectedJobFilters(draftJobFilters);
        setSelectedStoreFilters(draftStoreFilters);
        setSelectedRegionFilters(draftRegionFilters);
        setSelectedRecruiterFilters(draftRecruiterFilters);
        setAppliedFilters((current) => ({
            ...current,
            jobIds,
            storeIds,
            regionIds,
            mainRecruiterEmails,
            startedByCurrentUser: draftStartedByCurrentUser
        }));
        closeJobFilterDialog();
    }, [
        closeJobFilterDialog,
        draftJobFilters,
        draftRegionFilters,
        draftRecruiterFilters,
        draftStoreFilters,
        draftStartedByCurrentUser
    ]);

    const clearJobFilters = React.useCallback(() => {
        conversationsRequestRef.current += 1;
        setDraftJobFilters([]);
        setDraftStoreFilters([]);
        setDraftRegionFilters([]);
        setDraftRecruiterFilters([]);
        setDraftStartedByCurrentUser(false);
        setSelectedJobFilters([]);
        setSelectedStoreFilters([]);
        setSelectedRegionFilters([]);
        setSelectedRecruiterFilters([]);
        setAppliedFilters((current) => ({
            ...current,
            jobIds: [],
            jobCodes: [],
            storeIds: [],
            regionIds: [],
            mainRecruiterEmails: [],
            startedByCurrentUser: false
        }));
    }, []);

    const clearDraftJobFilters = React.useCallback(() => {
        setDraftJobFilters([]);
        setDraftStoreFilters([]);
        setDraftRegionFilters([]);
        setDraftRecruiterFilters([]);
        setDraftStartedByCurrentUser(false);
    }, []);

    const removeJobFilter = React.useCallback((jobId: string) => {
        conversationsRequestRef.current += 1;
        setSelectedJobFilters((current) =>
            current.filter((job) => String(job.jobId) !== jobId)
        );
        setAppliedFilters((current) => ({
            ...current,
            jobIds: current.jobIds.filter((item) => item !== jobId)
        }));
    }, []);

    const removeStoreFilter = React.useCallback((storeValue: string) => {
        conversationsRequestRef.current += 1;
        setSelectedStoreFilters((current) =>
            current.filter((store) => String(store.value) !== storeValue)
        );
        setAppliedFilters((current) => ({
            ...current,
            storeIds: current.storeIds.filter((item) => item !== storeValue)
        }));
    }, []);

    const removeRegionFilter = React.useCallback((regionValue: string) => {
        conversationsRequestRef.current += 1;
        setSelectedRegionFilters((current) =>
            current.filter((region) => String(region.value) !== regionValue)
        );
        setAppliedFilters((current) => ({
            ...current,
            regionIds: current.regionIds.filter((item) => item !== regionValue)
        }));
    }, []);

    const removeRecruiterFilter = React.useCallback(
        (recruiterEmail: string) => {
            conversationsRequestRef.current += 1;
            setSelectedRecruiterFilters((current) =>
                current.filter(
                    (recruiter) =>
                        String(recruiter.email || recruiter.value) !==
                        recruiterEmail
                )
            );
            setAppliedFilters((current) => ({
                ...current,
                mainRecruiterEmails: current.mainRecruiterEmails.filter(
                    (item) => item !== recruiterEmail
                )
            }));
        },
        []
    );

    const clearStartedByCurrentUserFilter = React.useCallback(() => {
        conversationsRequestRef.current += 1;
        setAppliedFilters((current) => ({
            ...current,
            startedByCurrentUser: false
        }));
    }, []);

    const updateDraftFilters = React.useCallback(
        (
            draft: {
                jobs: Compleo.IObject[];
                stores: WhatsappInboxFilterOption[];
                regions: WhatsappInboxFilterOption[];
                recruiters: WhatsappInboxFilterOption[];
                startedByCurrentUser: boolean;
            },
            changedFilter:
                | "JOB"
                | "STORE"
                | "REGION"
                | "RECRUITER"
                | "STARTED_BY"
        ) => {
            const compatible = reconcileWhatsappInboxDraftFilters(
                draft,
                changedFilter
            );
            setDraftJobFilters(compatible.jobs);
            setDraftStoreFilters(compatible.stores);
            setDraftRegionFilters(compatible.regions);
            setDraftRecruiterFilters(compatible.recruiters);
            setDraftStartedByCurrentUser(compatible.startedByCurrentUser);
        },
        []
    );

    const currentDraftFilters = () => ({
        jobs: draftJobFilters,
        stores: draftStoreFilters,
        regions: draftRegionFilters,
        recruiters: draftRecruiterFilters,
        startedByCurrentUser: draftStartedByCurrentUser
    });

    const toggleDraftJob = (job: Compleo.IObject) => {
        const jobId = String(job.jobId || "");
        if (!jobId) {
            return;
        }

        const draft = currentDraftFilters();
        updateDraftFilters(
            {
                ...draft,
                jobs: draft.jobs.some((item) => String(item.jobId) === jobId)
                    ? draft.jobs.filter((item) => String(item.jobId) !== jobId)
                    : [...draft.jobs, job]
            },
            "JOB"
        );
    };

    const toggleDraftStore = (store: WhatsappInboxFilterOption) => {
        const storeValue = String(store.value || "");
        if (!storeValue) {
            return;
        }

        const draft = currentDraftFilters();
        updateDraftFilters(
            {
                ...draft,
                stores: draft.stores.some(
                    (item) => String(item.value) === storeValue
                )
                    ? draft.stores.filter(
                          (item) => String(item.value) !== storeValue
                      )
                    : [...draft.stores, store]
            },
            "STORE"
        );
    };

    const toggleDraftRegion = (region: WhatsappInboxFilterOption) => {
        const regionValue = String(region.value || "");
        if (!regionValue) {
            return;
        }

        const draft = currentDraftFilters();
        updateDraftFilters(
            {
                ...draft,
                regions: draft.regions.some(
                    (item) => String(item.value) === regionValue
                )
                    ? draft.regions.filter(
                          (item) => String(item.value) !== regionValue
                      )
                    : [...draft.regions, region]
            },
            "REGION"
        );
    };

    const toggleDraftRecruiter = (recruiter: WhatsappInboxFilterOption) => {
        const recruiterEmail = String(recruiter.email || recruiter.value || "");
        if (!recruiterEmail) {
            return;
        }

        const draft = currentDraftFilters();
        updateDraftFilters(
            {
                ...draft,
                recruiters: draft.recruiters.some(
                    (item) =>
                        String(item.email || item.value) === recruiterEmail
                )
                    ? draft.recruiters.filter(
                          (item) =>
                              String(item.email || item.value) !==
                              recruiterEmail
                      )
                    : [...draft.recruiters, recruiter]
            },
            "RECRUITER"
        );
    };

    const changeDraftStartedByCurrentUser = (value: boolean) => {
        const draft = currentDraftFilters();
        updateDraftFilters(
            { ...draft, startedByCurrentUser: value },
            "STARTED_BY"
        );
    };

    const selectConversation = (
        conversation: Compleo.IObject,
        useInboxFilters = true
    ) => {
        const identity = getWhatsappInboxConversationIdentity(conversation);
        const timelineKey = getWhatsappInboxTimelineKey(conversation);
        const shouldRefreshSelectedTimeline =
            selectedConversationKeyRef.current === timelineKey;
        if (
            !timelineKey ||
            shouldIgnoreWhatsappConversationSelection(
                selectedConversationKeyRef.current,
                timelineKey,
                selectedConversationUsesInboxFiltersRef.current,
                useInboxFilters
            )
        ) {
            return;
        }

        conversationRequestRef.current += 1;
        selectedApplicantIdRef.current = identity.applicantId;
        selectedSegmentIdRef.current = identity.applicantId
            ? ""
            : identity.segmentId;
        selectedConversationKeyRef.current = timelineKey;
        selectedConversationUsesInboxFiltersRef.current = useInboxFilters;
        messageSearchRequestRef.current += 1;
        sendRequestRef.current += 1;
        tagSaveRequestRef.current += 1;
        setSelectedApplicantId(identity.applicantId);
        setSelectedSegmentId(
            identity.applicantId ? "" : identity.segmentId
        );
        setConversations((current) =>
            markWhatsappInboxTimelineAsRead(current, timelineKey)
        );
        resetConversationState();
        if (shouldRefreshSelectedTimeline) {
            refreshConversationRef.current(
                identity.applicantId,
                identity.applicantId ? "" : identity.segmentId
            );
        }
    };

    const openContactDialog = () => {
        setContactSearch("");
        setContactResults([]);
        setContactSearchExecuted(false);
        setContactLoading(false);
        setContactDialogOpen(true);
    };

    const closeContactDialog = () => {
        contactSearchRequestRef.current += 1;
        setContactLoading(false);
        setContactDialogOpen(false);
    };

    const getReplyPreviewText = React.useCallback(
        (contextMessageId: any) => {
            const repliedItem = itemsByMessageId.get(String(contextMessageId));
            if (!repliedItem) {
                return t("previousMessage");
            }

            if (isWhatsappMediaItem(repliedItem)) {
                return getWhatsappMediaLabel(repliedItem, t);
            }

            return (
                getItemText(repliedItem, {
                    reactedWith: t("reactedWith")
                }) || t("previousMessage")
            );
        },
        [itemsByMessageId, t]
    );

    const handleSearchMessages = React.useCallback(
        async (searchText = messageSearch) => {
            const text = searchText.trim();
            if (!companyId || !selectedConversationKey || text.length < 4) {
                messageSearchRequestRef.current += 1;
                setMessageSearchResults([]);
                setMessageSearchExecuted(false);
                setMessageSearchLoading(false);
                return;
            }

            const requestId = messageSearchRequestRef.current + 1;
            messageSearchRequestRef.current = requestId;
            setMessageSearchLoading(true);
            setMessageSearchExecuted(true);
            const conversationKey = selectedConversationKey;
            const requestScope = {
                companyId,
                applicantId: selectedApplicantId,
                segmentId: selectedSegmentId,
                conversationKey
            };

            try {
                const result = await apiDirectCall(
                    "/whatsapp/searchmessages",
                    "post",
                    {
                        companyId,
                        ...buildWhatsappInboxTimelineRequest(
                            selectedApplicantId,
                            selectedSegmentId
                        ),
                        search: text,
                        includeInternalNotes: true,
                        limit: 100,
                        contactEpisodeCapability: "CONTACT_EPISODE_V1"
                    }
                );
                if (
                    messageSearchRequestRef.current === requestId &&
                    isWhatsappInboxRequestScopeCurrent(
                        requestScope,
                        getCurrentInboxRequestScope()
                    )
                ) {
                    setMessageSearchResults(
                        getWhatsappApiResponseData(result).matches || []
                    );
                }
            } catch (error) {
                if (
                    messageSearchRequestRef.current === requestId &&
                    isWhatsappInboxRequestScopeCurrent(
                        requestScope,
                        getCurrentInboxRequestScope()
                    )
                ) {
                    notify(
                        getErrorMessage(
                            error,
                            tRef.current("searchMessagesError")
                        ),
                        "error"
                    );
                }
            } finally {
                if (
                    messageSearchRequestRef.current === requestId &&
                    isWhatsappInboxRequestScopeCurrent(
                        requestScope,
                        getCurrentInboxRequestScope()
                    )
                ) {
                    setMessageSearchLoading(false);
                }
            }
        },
        [
            companyId,
            getCurrentInboxRequestScope,
            messageSearch,
            notify,
            selectedApplicantId,
            selectedConversationKey,
            selectedSegmentId
        ]
    );

    React.useEffect(() => {
        if (!selectedConversationKey) {
            return;
        }

        const text = messageSearch.trim();
        if (text.length < 4) {
            messageSearchRequestRef.current += 1;
            setMessageSearchResults((current) =>
                current.length > 0 ? [] : current
            );
            setMessageSearchExecuted(false);
            setMessageSearchLoading(false);
            return;
        }

        const timer = window.setTimeout(() => {
            handleSearchMessages(text);
        }, 300);

        return () => {
            window.clearTimeout(timer);
        };
    }, [handleSearchMessages, messageSearch, selectedConversationKey]);

    const handleSendSession = async () => {
        const text = composerText.trim();
        const file = composerFile;
        const operationCompanyId = companyId;
        const applicantId = selectedApplicantId;
        const actionTarget = getWhatsappInboxActionTarget(
            applicantId,
            actionContext
        );
        const conversationKey = selectedConversationKey;
        const requestScope = {
            companyId: operationCompanyId,
            applicantId,
            segmentId: selectedSegmentId,
            conversationKey
        };
        const contactEpisodeActionContext = getWhatsappContactEpisodeActionContextForMode(
            whatsappContactEpisodeMode,
            activeContactEpisodeActionContext
        );
        const replyToMessageId = resolveWhatsappReplyToMessageId(
            replyTo,
            contactEpisodeActionContext
        );
        if (replyToMessageId === undefined) {
            return;
        }
        if (
            (!text && !file) ||
            !operationCompanyId ||
            !conversationKey ||
            (!actionContext && !contactEpisodeActionContext)
        ) {
            return;
        }
        const attempt = actionRequestTrackerRef.current.prepare(
            "SEND_SESSION",
            JSON.stringify({
                companyId: operationCompanyId,
                applicantId,
                segmentId: actionContext?.segmentId || "",
                conversationKey,
                text,
                file: file
                    ? {
                          name: file.name,
                          type: file.type,
                          size: file.size,
                          lastModified: file.lastModified
                      }
                    : null,
                replyToMessageId,
                contactEpisodeId:
                    contactEpisodeActionContext?.contactEpisodeId || "",
                resolutionRevision:
                    contactEpisodeActionContext?.resolutionRevision || 0
            })
        );
        const actionRequestId = attempt.actionRequestId;
        if (!canSendSession) {
            notify(
                optedOut
                    ? t("candidateOptOut")
                    : t("sessionWindowClosedTemplate"),
                "error"
            );
            return;
        }

        const sendRequestId = sendRequestRef.current + 1;
        sendRequestRef.current = sendRequestId;
        setSending(true);
        try {
            if (file) {
                const uploadResponse = await apiDirectCall(
                    "/whatsapp/requestmediaupload",
                    "post",
                    buildWhatsappContactEpisodeActionRequest(
                    {
                        companyId: operationCompanyId,
                        ...actionTarget,
                        originalFileName: file.name,
                        contentType: file.type,
                        fileSize: file.size,
                        replyToMessageId
                        },
                        contactEpisodeActionContext,
                        actionRequestId
                    )
                );
                const uploadData = getWhatsappApiResponseData(uploadResponse);
                if (uploadData.uploadMethod === "POST") {
                    const formData = new FormData();
                    Object.entries(
                        uploadData.uploadFields || {}
                    ).forEach(([key, value]) =>
                        formData.append(key, String(value))
                    );
                    formData.append("file", file);
                    await axios.post(uploadData.uploadURL, formData);
                } else {
                    await axios.put(uploadData.uploadURL, file, {
                        headers: {
                            "Content-Type": uploadData.contentType || file.type,
                            "x-amz-tagging": `originalFileName=${encodeURIComponent(
                                uploadData.originalFileName || file.name
                            )}`
                        }
                    });
                }
                await apiDirectCall(
                    "/whatsapp/sendmedia",
                    "post",
                    buildWhatsappContactEpisodeActionRequest(
                        {
                    companyId: operationCompanyId,
                    ...actionTarget,
                    attachmentId: uploadData.attachmentId,
                    s3Key: uploadData.s3Key,
                            originalFileName:
                                uploadData.originalFileName || file.name,
                    contentType: uploadData.contentType || file.type,
                    fileSize: file.size,
                    caption: text,
                    replyToMessageId
                        },
                        contactEpisodeActionContext,
                        actionRequestId
                    )
                );
            } else {
                await apiDirectCall(
                    "/whatsapp/sendsessionmessage",
                    "post",
                    buildWhatsappContactEpisodeActionRequest(
                        {
                    companyId: operationCompanyId,
                    ...actionTarget,
                    text,
                    replyToMessageId
                        },
                        contactEpisodeActionContext,
                        actionRequestId
                    )
                );
            }
            actionRequestTrackerRef.current.complete(
                "SEND_SESSION",
                attempt.signature
            );
            const isSameCompany = companyIdRef.current === operationCompanyId;
            const isCurrentSend = sendRequestRef.current === sendRequestId;
            const isCurrentConversation =
                isSameCompany &&
                isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                );

            if (isCurrentSend && isCurrentConversation) {
                setComposerText("");
                setComposerFile(null);
                setReplyTo(null);
                await refreshConversation(applicantId);
            }
            if (isCurrentSend && isSameCompany) {
                await refreshConversations();
            }
        } catch (error) {
            if (
                sendRequestRef.current === sendRequestId &&
                isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                notify(getErrorMessage(error, t("sendMessageError")), "error");
            }
        } finally {
            if (
                sendRequestRef.current === sendRequestId &&
                isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                setSending(false);
            }
        }
    };

    const handleComposerKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (event.key !== "Enter" || event.shiftKey) {
            return;
        }

        event.preventDefault();
        if (
            !canSendSession ||
            sending ||
            (!composerText.trim() && !composerFile)
        ) {
            return;
        }

        void handleSendSession();
    };

    const handleReplyToMessage = (item: Compleo.IObject) => {
        if (
            !isWhatsappReplyTargetInActionContext(
                item,
                activeContactEpisodeActionContext
            )
        ) {
            return;
        }
        setReplyTo(item);
        focusComposerInput();
    };

    const handleOpenWhatsappMedia = async (item: Compleo.IObject) => {
        if (!selectedConversationKey || !item.messageId) {
            return;
        }

        const mediaWindow = window.open("", "_blank");
        if (mediaWindow) {
            mediaWindow.opener = null;
        }
        const actionContext = getWhatsappContactEpisodeActionContext(item);
        const requestScope = {
            companyId,
            applicantId: selectedApplicantId,
            segmentId: selectedSegmentId,
            conversationKey: selectedConversationKey
        };
        const attempt = actionRequestTrackerRef.current.prepare(
            "OPEN_MEDIA",
            JSON.stringify({
                companyId,
                applicantId: selectedApplicantId,
                segmentId: selectedSegmentId || "",
                messageId: item.messageId,
                contactEpisodeId: actionContext?.contactEpisodeId || "",
                resolutionRevision: actionContext?.resolutionRevision || 0
            })
        );

        try {
            const response = await apiDirectCall(
                "/whatsapp/mediaurl",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                    {
                companyId,
                applicantId: selectedApplicantId,
                segmentId:
                    getWhatsappMessageActionContext(item).segmentId ||
                    undefined,
                messageId: item.messageId
                    },
                    actionContext,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "OPEN_MEDIA",
                attempt.signature
            );
            if (
                !isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                mediaWindow?.close();
                return;
            }
            const data = getWhatsappApiResponseData(response);
            if (mediaWindow) {
                mediaWindow.location.href = data.url;
            } else {
                window.open(data.url, "_blank", "noopener,noreferrer");
            }
        } catch (error) {
            if (mediaWindow) {
                mediaWindow.close();
            }
            if (
                isWhatsappInboxRequestScopeCurrent(
                    requestScope,
                    getCurrentInboxRequestScope()
                )
            ) {
                notify(getErrorMessage(error, t("openMediaError")), "error");
            }
        }
    };

    const handleOpenTemplateDialog = () => {
        if (!selectedApplicantId || !actionContext) {
            return;
        }
        if (optedOut) {
            notify(t("candidateOptOut"), "error");
            return;
        }
        if (!canStartWithTemplate) {
            notify(t("sendBlockedNoTemplateEligibility"), "error");
            return;
        }

        setTemplateDialogOpen(true);
    };

    const handleTemplateSent = async () => {
        const applicantId = selectedApplicantIdRef.current;
        if (getWhatsappInboxTimelineKey({ applicantId })) {
            await refreshConversation(applicantId);
        }
        await refreshConversations();
    };

    const handleAddNote = async () => {
        const text = noteText.trim();
        const operationCompanyId = companyId;
        const applicantId = selectedApplicantId;
        const actionTarget = getWhatsappInboxActionTarget(
            applicantId,
            actionContext
        );
        const conversationKey = selectedConversationKey;
        if (!text || !operationCompanyId || !conversationKey || !actionContext) {
            return;
        }
        const attempt = actionRequestTrackerRef.current.prepare(
            "ADD_NOTE",
            JSON.stringify({
                companyId: operationCompanyId,
                applicantId,
                segmentId: actionContext?.segmentId || "",
                conversationKey,
                text,
                contactEpisodeId:
                    activeContactEpisodeActionContext?.contactEpisodeId || "",
                resolutionRevision:
                    activeContactEpisodeActionContext?.resolutionRevision || 0
            })
        );

        try {
            await apiDirectCall(
                "/whatsapp/addinternalnote",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                    {
                companyId: operationCompanyId,
                ...actionTarget,
                text
                    },
                    activeContactEpisodeActionContext,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "ADD_NOTE",
                attempt.signature
            );
            if (
                companyIdRef.current === operationCompanyId &&
                selectedConversationKeyRef.current === conversationKey
            ) {
                setNoteText("");
                await refreshConversation(applicantId);
            }
        } catch (error) {
            if (
                companyIdRef.current === operationCompanyId &&
                selectedConversationKeyRef.current === conversationKey
            ) {
                notify(getErrorMessage(error, t("saveNoteError")), "error");
            }
        }
    };

    const persistCandidateTags = async (nextTags: string[]) => {
        const operationCompanyId = companyId;
        const applicantId = selectedApplicantId;
        const actionTarget = getWhatsappInboxActionTarget(
            applicantId,
            actionContext
        );
        const conversationKey = selectedConversationKey;
        if (!operationCompanyId || !applicantId || !actionContext) {
            return;
        }

        const tags = normalizeTagList(nextTags, language);
        const attempt = actionRequestTrackerRef.current.prepare(
            "UPDATE_TAGS",
            JSON.stringify({
                companyId: operationCompanyId,
                applicantId,
                segmentId: actionTarget.segmentId || "",
                conversationKey,
                tags,
                contactEpisodeId:
                    activeContactEpisodeActionContext?.contactEpisodeId || "",
                resolutionRevision:
                    activeContactEpisodeActionContext?.resolutionRevision || 0
            })
        );

        const tagSaveRequestId = tagSaveRequestRef.current + 1;
        tagSaveRequestRef.current = tagSaveRequestId;
        setTagSaving(true);

        try {
            const result = await apiDirectCall(
                "/whatsapp/updateapplicanttags",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                {
                    companyId: operationCompanyId,
                    ...actionTarget,
                    tags
                    },
                    activeContactEpisodeActionContext,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "UPDATE_TAGS",
                attempt.signature
            );
            const updatedTags = getWhatsappApiResponseData(result).tags || tags;
            const isSameCompany = companyIdRef.current === operationCompanyId;
            const isCurrentSave =
                tagSaveRequestRef.current === tagSaveRequestId;
            const isCurrentConversation =
                isSameCompany &&
                selectedConversationKeyRef.current === conversationKey;

            if (isSameCompany) {
                setKnownTags((current) =>
                    moveTagsToFront(current, updatedTags, language)
                );
                setConversations((current) =>
                    current.map((conversation) =>
                        getWhatsappInboxTimelineKey(conversation) ===
                        conversationKey
                            ? {
                                  ...conversation,
                                  tags: updatedTags
                              }
                            : conversation
                    )
                );
                await refreshConversations();
            }
            if (isCurrentConversation) {
                if (isCurrentSave) {
                    setTagEditorMode("closed");
                    setTagEditorValue("");
                    setTagEditorOriginal("");
                }
                await refreshConversation(applicantId);
                notify(t("tagsUpdated"), "success");
            }
        } catch (error) {
            if (
                tagSaveRequestRef.current === tagSaveRequestId &&
                companyIdRef.current === operationCompanyId &&
                selectedConversationKeyRef.current === conversationKey
            ) {
                notify(getErrorMessage(error, t("updateTagsError")), "error");
            }
        } finally {
            if (
                tagSaveRequestRef.current === tagSaveRequestId &&
                companyIdRef.current === operationCompanyId &&
                selectedConversationKeyRef.current === conversationKey
            ) {
                setTagSaving(false);
            }
        }
    };

    const openAddTagEditor = () => {
        setTagEditorMode("add");
        setTagEditorOriginal("");
        setTagEditorValue("");
    };

    const openEditTagEditor = (tag: string) => {
        setTagEditorMode("edit");
        setTagEditorOriginal(tag);
        setTagEditorValue(tag);
    };

    const closeTagEditor = () => {
        setTagEditorMode("closed");
        setTagEditorOriginal("");
        setTagEditorValue("");
    };

    const submitTagEditor = () => {
        if (tagSaving) {
            return;
        }

        const nextValue = tagEditorValue.trim();
        if (!nextValue) {
            notify(t("tagNameRequired"), "error");
            return;
        }

        if (tagEditorMode === "add") {
            void persistCandidateTags([...candidateTags, nextValue]);
            return;
        }

        if (tagEditorMode === "edit") {
            void persistCandidateTags(
                candidateTags.map((tag) =>
                    tag === tagEditorOriginal ? nextValue : tag
                )
            );
        }
    };

    const removeCandidateTag = (tag: string) => {
        if (tagSaving) {
            return;
        }

        void persistCandidateTags(candidateTags.filter((item) => item !== tag));
    };

    const handleSearchContacts = React.useCallback(
        async (searchText = contactSearch) => {
            const text = searchText.trim();
            if (!companyId || text.length < 4) {
                setContactResults((current) =>
                    current.length > 0 ? [] : current
                );
                setContactSearchExecuted(false);
                setContactLoading(false);
                return;
            }

            const requestId = contactSearchRequestRef.current + 1;
            contactSearchRequestRef.current = requestId;
            setContactLoading(true);
            setContactSearchExecuted(true);

            try {
                const result = await apiDirectCall(
                    "/whatsapp/searchcontacts",
                    "post",
                    {
                        companyId,
                        search: text,
                        jobIds: appliedFilters.jobIds
                    }
                );
                if (contactSearchRequestRef.current === requestId) {
                    setContactResults(
                        getWhatsappApiResponseData(result).results || []
                    );
                }
            } catch (error) {
                if (contactSearchRequestRef.current === requestId) {
                    notify(
                        getErrorMessage(
                            error,
                            tRef.current("searchCandidateError")
                        ),
                        "error"
                    );
                }
            } finally {
                if (contactSearchRequestRef.current === requestId) {
                    setContactLoading(false);
                }
            }
        },
        [appliedFilters.jobIds, companyId, contactSearch, notify]
    );

    React.useEffect(() => {
        if (!contactDialogOpen) {
            return;
        }

        const text = contactSearch.trim();
        if (text.length < 4) {
            contactSearchRequestRef.current += 1;
            setContactResults((current) => (current.length > 0 ? [] : current));
            setContactSearchExecuted(false);
            setContactLoading(false);
            return;
        }

        const timer = window.setTimeout(() => {
            handleSearchContacts(text);
        }, 250);

        return () => {
            window.clearTimeout(timer);
        };
    }, [contactDialogOpen, contactSearch, handleSearchContacts]);

    const handleSearchJobs = React.useCallback(
        async (searchText = jobSearch) => {
            const text = searchText.trim();
            if (!companyId || text.length < 4) {
                setJobResults((current) => (current.length > 0 ? [] : current));
                setJobSearchExecuted(false);
                setJobLoading(false);
                return;
            }

            const requestId = jobSearchRequestRef.current + 1;
            jobSearchRequestRef.current = requestId;
            setJobLoading(true);
            setJobSearchExecuted(true);

            try {
                const result = await apiDirectCall(
                    "/whatsapp/searchjobs",
                    "post",
                    {
                        companyId,
                        search: text,
                        limit: 20
                    }
                );
                if (jobSearchRequestRef.current === requestId) {
                    setJobResults(
                        getWhatsappApiResponseData(result).jobs || []
                    );
                }
            } catch (error) {
                if (jobSearchRequestRef.current === requestId) {
                    notify(
                        getErrorMessage(error, tRef.current("searchJobsError")),
                        "error"
                    );
                }
            } finally {
                if (jobSearchRequestRef.current === requestId) {
                    setJobLoading(false);
                }
            }
        },
        [companyId, jobSearch, notify]
    );

    const changeJobSearch = React.useCallback((value: string) => {
        jobSearchRequestRef.current += 1;
        setJobSearch(value);
        setJobResults([]);
        setJobSearchExecuted(false);
        setJobLoading(false);
    }, []);

    React.useEffect(() => {
        if (!jobFilterOpen) {
            return;
        }

        const text = jobSearch.trim();
        if (text.length < 4) {
            jobSearchRequestRef.current += 1;
            setJobResults((current) => (current.length > 0 ? [] : current));
            setJobSearchExecuted(false);
            setJobLoading(false);
            return;
        }

        const timer = window.setTimeout(() => {
            handleSearchJobs(text);
        }, 250);

        return () => {
            window.clearTimeout(timer);
        };
    }, [handleSearchJobs, jobFilterOpen, jobSearch]);

    React.useEffect(() => {
        setTagEditorMode("closed");
        setTagEditorOriginal("");
        setTagEditorValue("");
    }, [candidate.applicantId]);

    if (!readyTranslation) {
        return (
            <Box className={classes.page} style={embeddedPageStyle}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (!whatsappEnabled) {
        return (
            <Box className={classes.page} style={embeddedPageStyle}>
                <Alert severity="info">{t("whatsappDisabled")}</Alert>
            </Box>
        );
    }

    return (
        <Box className={classes.page} style={embeddedPageStyle}>
            <Paper className={classes.shell}>
                <Box className={classes.inboxLayout}>
                    <Box className={classes.listPane}>
                        <Box className={classes.header}>
                            <Box display="flex" alignItems="center" gridGap={8}>
                                <ChatIcon />
                                <Typography variant="h6">WhatsApp</Typography>
                                <Box flex={1} />
                                <Tooltip title={t("filterConversations")}>
                                    <IconButton
                                        size="small"
                                        color={
                                            hasAppliedConversationFilters
                                                ? "primary"
                                                : "default"
                                        }
                                        onClick={openJobFilterDialog}
                                        aria-label={t("filterConversations")}
                                    >
                                        <FilterListIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t("refreshConversations")}>
                                    <IconButton
                                        size="small"
                                        onClick={() => refreshConversations()}
                                        aria-label={t("refreshConversations")}
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box display="flex" gridGap={8} marginTop={1}>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder={t("searchConversation")}
                                    inputProps={{
                                        "aria-label": t("searchConversation")
                                    }}
                                    value={search}
                                    onChange={(event) =>
                                        setSearch(event.target.value)
                                    }
                                    onKeyDown={(event) =>
                                        applyOnEnter(
                                            event,
                                            applyConversationSearch
                                        )
                                    }
                                    fullWidth
                                />
                                <Tooltip title={t("searchConversation")}>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            applyConversationSearch()
                                        }
                                        aria-label={t("searchConversation")}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    onClick={openContactDialog}
                                >
                                    {t("newConversation")}
                                </Button>
                            </Box>
                            {hasAppliedConversationFilters && (
                                <Box className={classes.filterSummary}>
                                    {selectedJobFilters.map((job) => (
                                        <Chip
                                            key={String(job.jobId)}
                                            size="small"
                                            label={getJobLabel(job, language)}
                                            onDelete={() =>
                                                removeJobFilter(
                                                    String(job.jobId)
                                                )
                                            }
                                        />
                                    ))}
                                    {selectedStoreFilters.map((store) => (
                                        <Chip
                                            key={String(store.value)}
                                            size="small"
                                            label={store.label}
                                            onDelete={() =>
                                                removeStoreFilter(
                                                    String(store.value)
                                                )
                                            }
                                        />
                                    ))}
                                    {selectedRegionFilters.map((region) => (
                                        <Chip
                                            key={String(region.value)}
                                            size="small"
                                            label={region.label}
                                            onDelete={() =>
                                                removeRegionFilter(
                                                    String(region.value)
                                                )
                                            }
                                        />
                                    ))}
                                    {selectedRecruiterFilters.map(
                                        (recruiter) => {
                                            const recruiterEmail = String(
                                                recruiter.email ||
                                                    recruiter.value ||
                                                    ""
                                            );
                                            return (
                                                <Chip
                                                    key={recruiterEmail}
                                                    size="small"
                                                    label={recruiter.label}
                                                    onDelete={() =>
                                                        removeRecruiterFilter(
                                                            recruiterEmail
                                                        )
                                                    }
                                                />
                                            );
                                        }
                                    )}
                                    {appliedFilters.startedByCurrentUser && (
                                        <Chip
                                            size="small"
                                            label={t("startedByMe")}
                                            onDelete={
                                                clearStartedByCurrentUserFilter
                                            }
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.inboxToolbar}>
                            <Box className={classes.tagRail}>
                                <Chip
                                    size="small"
                                    label={t("allTags")}
                                    color={
                                        appliedFilters.tags.length === 0
                                            ? "primary"
                                            : "default"
                                    }
                                    onClick={clearTagFilters}
                                />
                                {availableTags.map((tag) => {
                                    const selected = appliedFilters.tags.includes(
                                        tag
                                    );
                                    const tagColor = getTagColor(tag);
                                    return (
                                        <Chip
                                            key={tag}
                                            size="small"
                                            label={tag}
                                            icon={<LabelIcon />}
                                            onClick={() => toggleTagFilter(tag)}
                                            style={{
                                                backgroundColor: selected
                                                    ? tagColor.color
                                                    : tagColor.background,
                                                color: selected
                                                    ? "#ffffff"
                                                    : tagColor.color
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <List className={classes.scrollArea}>
                            {conversations.map((conversation) => {
                                const remotePhone =
                                    getWhatsappInboxPhoneDisplay(
                                        conversation.remotePhone,
                                        language
                                    );
                                const name =
                                    getWhatsappInboxConversationName(
                                        conversation.applicantName,
                                        conversation.applicantEmail,
                                        language
                                    ) ||
                                    t("candidate");
                                const phone = remotePhone || "-";
                                const tags = getConversationTags(
                                    conversation,
                                    language
                                ).slice(0, 3);
                                return (
                                    <ListItem
                                        key={getWhatsappInboxTimelineKey(
                                            conversation
                                        )}
                                        button
                                        className={`${
                                            classes.conversationItem
                                        } ${
                                            selectedConversationKey ===
                                            getWhatsappInboxTimelineKey(
                                                conversation
                                            )
                                                ? classes.selectedConversation
                                                : ""
                                        }`}
                                        onClick={() =>
                                            selectConversation(conversation)
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                className={
                                                    classes.conversationAvatar
                                                }
                                            >
                                                {getInitials(name)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <Box
                                                    className={
                                                        classes.conversationPrimary
                                                    }
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        className={
                                                            classes.conversationName
                                                        }
                                                        noWrap
                                                    >
                                                        {name}
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.conversationPhone
                                                        }
                                                    >
                                                        {phone}
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.conversationTime
                                                        }
                                                    >
                                                        {formatWhatsappInboxDate(
                                                            conversation.lastMessageAt,
                                                            language
                                                        )}
                                                    </Typography>
                                                    {conversation.unreadCount >
                                                        0 && (
                                                        <Chip
                                                            size="small"
                                                            color="primary"
                                                            className={
                                                                classes.unreadChip
                                                            }
                                                            label={
                                                                conversation.unreadCount
                                                            }
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        className={
                                                            classes.conversationPreview
                                                        }
                                                    >
                                                        {conversation.lastMessagePreview ||
                                                            t(
                                                                "noMessagesPreview"
                                                            )}
                                                    </Typography>
                                                    {tags.length > 0 && (
                                                        <Box
                                                            className={
                                                                classes.miniTagRow
                                                            }
                                                        >
                                                            {tags.map((tag) => {
                                                                const tagColor = getTagColor(
                                                                    tag
                                                                );
                                                                return (
                                                                    <Chip
                                                                        key={
                                                                            tag
                                                                        }
                                                                        size="small"
                                                                        label={
                                                                            tag
                                                                        }
                                                                        className={
                                                                            classes.miniTag
                                                                        }
                                                                        style={{
                                                                            backgroundColor:
                                                                                tagColor.background,
                                                                            color:
                                                                                tagColor.color
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                            {!loadingList && conversations.length === 0 && (
                                <ListItem>
                                    <Typography className={classes.muted}>
                                        {t("noConversationsFound")}
                                    </Typography>
                                </ListItem>
                            )}
                            {nextCursor && (
                                <ListItem>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        disabled={loadingList}
                                        onClick={() =>
                                            refreshConversations(nextCursor)
                                        }
                                    >
                                        {t("loadMore")}
                                    </Button>
                                </ListItem>
                            )}
                        </List>
                    </Box>
                    <Box className={classes.threadPane}>
                        <Box className={classes.header}>
                            <Typography variant="subtitle1">
                                {getFirstDisplayText(
                                    language,
                                    candidate.name,
                                    selectedConversation.applicantName
                                ) || t("selectConversation")}
                            </Typography>
                            {selectedConversationKey && (
                                <Box className={classes.chips}>
                                    <Chip
                                        size="small"
                                        label={
                                            optedOut
                                                ? t("optOut")
                                                : optedIn
                                                ? t("optIn")
                                                : t("noOptIn")
                                        }
                                    />
                                    <Chip
                                        size="small"
                                        label={
                                            hasWindow
                                                ? t("windowOpen")
                                                : t("windowClosed")
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        {selectedConversationKey ? (
                            <>
                                <Box className={classes.messages}>
                                    <Box
                                        display="flex"
                                        gridGap={8}
                                        marginBottom={2}
                                    >
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            placeholder={t(
                                                "searchThisConversation"
                                            )}
                                            inputProps={{
                                                "aria-label": t(
                                                    "searchThisConversation"
                                                )
                                            }}
                                            value={messageSearch}
                                            onChange={(event) =>
                                                setMessageSearch(
                                                    event.target.value
                                                )
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    handleSearchMessages();
                                                }
                                            }}
                                            fullWidth
                                        />
                                        <Tooltip title={t("searchMessages")}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label={t(
                                                        "searchMessages"
                                                    )}
                                                    disabled={
                                                        messageSearchLoading
                                                    }
                                                    onClick={() =>
                                                        handleSearchMessages()
                                                    }
                                                >
                                                    {messageSearchLoading ? (
                                                        <CircularProgress
                                                            size={18}
                                                        />
                                                    ) : (
                                                        <SearchIcon />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                    {visibleItems.map((item) => {
                                        const isOutgoing =
                                            item.direction === "OUTGOING";
                                        const isNote =
                                            item.docType ===
                                            "whatsapp-internal-note";
                                        return (
                                            <Box
                                                key={
                                                    item.messageId ||
                                                    item.noteId
                                                }
                                                className={`${
                                                    classes.messageRow
                                                } ${
                                                    isOutgoing
                                                        ? classes.outgoingRow
                                                        : ""
                                                }`}
                                            >
                                                <Box
                                                    className={`${
                                                        classes.bubble
                                                    } ${
                                                        isOutgoing
                                                            ? classes.outgoingBubble
                                                            : ""
                                                    } ${
                                                        isNote
                                                            ? classes.noteBubble
                                                            : ""
                                                    }`}
                                                >
                                                    {item.contextMessageId && (
                                                        <Typography
                                                            variant="caption"
                                                            className={`${classes.muted} ${classes.quotePreview}`}
                                                        >
                                                            {t("replyTo")}:{" "}
                                                            {getReplyPreviewText(
                                                                item.contextMessageId
                                                            )}
                                                        </Typography>
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        className={
                                                            classes.messageText
                                                        }
                                                    >
                                                        {isWhatsappMediaItem(
                                                            item
                                                        ) ? (
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gridGap={8}
                                                            >
                                                                <DescriptionIcon fontSize="small" />
                                                                <span>
                                                                    {getWhatsappMediaLabel(
                                                                        item,
                                                                        t
                                                                    )}
                                                                </span>
                                                            </Box>
                                                        ) : (
                                                            getItemText(item, {
                                                                reactedWith: t(
                                                                    "reactedWith"
                                                                )
                                                            })
                                                        )}
                                                    </Typography>
                                                    {isWhatsappMediaItem(
                                                        item
                                                    ) &&
                                                        item.caption && (
                                                            <Typography
                                                                variant="body2"
                                                                className={
                                                                    classes.messageText
                                                                }
                                                            >
                                                                {item.caption}
                                                            </Typography>
                                                        )}
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        gridGap={6}
                                                        marginTop={0.5}
                                                        flexWrap="wrap"
                                                    >
                                                        {isNote && (
                                                            <Chip
                                                                size="small"
                                                                label={t(
                                                                    "internalNote"
                                                                )}
                                                            />
                                                        )}
                                                        {item.sentByUserEmail && (
                                                            <Typography variant="caption">
                                                                {item.sentByUserName ||
                                                                    item.sentByUserEmail}
                                                            </Typography>
                                                        )}
                                                        {isNote &&
                                                            item.createdByUser && (
                                                                <Typography variant="caption">
                                                                    {
                                                                        item.createdByUser
                                                                    }
                                                                </Typography>
                                                            )}
                                                        <Typography variant="caption">
                                                            {formatWhatsappInboxDate(
                                                                item.metaTimestamp ||
                                                                    item.createdAt,
                                                                language
                                                            )}
                                                        </Typography>
                                                        {!isNote && (
                                                            <Tooltip
                                                                title={t(
                                                                    "replyMessage"
                                                                )}
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label={t(
                                                                        "replyMessage"
                                                                    )}
                                                                    disabled={
                                                                        !isWhatsappReplyTargetInActionContext(
                                                                            item,
                                                                            activeContactEpisodeActionContext
                                                                        )
                                                                    }
                                                                    onClick={() =>
                                                                        handleReplyToMessage(
                                                                            item
                                                                        )
                                                                    }
                                                                >
                                                                    <ReplyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {!isNote &&
                                                            canOpenWhatsappMedia(
                                                                item
                                                            ) && (
                                                                <Tooltip
                                                                    title={t(
                                                                        "openFile"
                                                                    )}
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        aria-label={t(
                                                                            "openFile"
                                                                        )}
                                                                        onClick={() =>
                                                                            handleOpenWhatsappMedia(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        <VisibilityIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                    {messageSearchExecuted &&
                                        !messageSearchLoading &&
                                        visibleItems.length === 0 && (
                                            <Typography
                                                className={classes.muted}
                                            >
                                                {t("noMessagesFound")}
                                            </Typography>
                                        )}
                                    <div ref={messagesEndRef} />
                                    {loadingConversation && (
                                        <Typography className={classes.muted}>
                                            {t("updatingConversation")}
                                        </Typography>
                                    )}
                                </Box>
                                <Box className={classes.composer}>
                                    <WhatsappInboxTimelineContinuation
                                        conversationData={conversationData}
                                        loadingConversation={
                                            loadingConversation
                                        }
                                        loadingMoreContexts={
                                            loadingMoreContexts
                                        }
                                        onLoadMore={loadMoreTimelineContexts}
                                        t={t}
                                    />
                                    {recipientPhone && (
                                        <Typography
                                            variant="caption"
                                            className={classes.muted}
                                        >
                                            {t("recipientPhone")}: {recipientPhone}
                                        </Typography>
                                    )}
                                    {!canSendSession &&
                                        selectedConversationKey && (
                                            <Alert severity="info">
                                                {optedOut
                                                    ? t("candidateOptOut")
                                                    : t(
                                                          "sessionWindowClosedResume"
                                                      )}
                                            </Alert>
                                        )}
                                    {selectedApplicantId && !hasWindow && (
                                        <Box marginTop={1}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<ChatIcon />}
                                                disabled={!canStartWithTemplate}
                                                onClick={
                                                    handleOpenTemplateDialog
                                                }
                                            >
                                                {t("sendTemplate")}
                                            </Button>
                                        </Box>
                                    )}
                                    {replyTo && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            marginTop={1}
                                        >
                                            <ReplyIcon fontSize="small" />
                                            <Typography
                                                variant="caption"
                                                style={{ marginLeft: 6 }}
                                            >
                                                {t("replyingTo")}{" "}
                                                {isWhatsappMediaItem(replyTo)
                                                    ? getWhatsappMediaLabel(
                                                          replyTo,
                                                          t
                                                      )
                                                    : getItemText(replyTo, {
                                                          reactedWith: t(
                                                              "reactedWith"
                                                          )
                                                      })}
                                            </Typography>
                                            <Tooltip title={t("cancelReply")}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={t(
                                                        "cancelReply"
                                                    )}
                                                    onClick={() =>
                                                        setReplyTo(null)
                                                    }
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box
                                        display="flex"
                                        gridGap={8}
                                        marginTop={1}
                                    >
                                        <input
                                            ref={composerFileInputRef}
                                            type="file"
                                            hidden
                                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/jpeg,image/png"
                                            onChange={(event) => {
                                                setComposerFile(
                                                    event.target.files?.[0] ||
                                                        null
                                                );
                                                event.target.value = "";
                                            }}
                                        />
                                        <Tooltip title={t("attachFile")}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        !canSendSession ||
                                                        sending
                                                    }
                                                    onClick={() =>
                                                        composerFileInputRef.current?.click()
                                                    }
                                                    aria-label={t("attachFile")}
                                                >
                                                    <AttachFileIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            inputRef={composerInputRef}
                                            placeholder={
                                                composerFile
                                                    ? t("caption")
                                                    : t("message")
                                            }
                                            inputProps={{
                                                "aria-label": composerFile
                                                    ? t("caption")
                                                    : t("message")
                                            }}
                                            value={composerText}
                                            onChange={(event) =>
                                                setComposerText(
                                                    event.target.value
                                                )
                                            }
                                            onKeyDown={handleComposerKeyDown}
                                            disabled={
                                                !canSendSession || sending
                                            }
                                            fullWidth
                                            multiline
                                            rowsMax={4}
                                        />
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={
                                                !canSendSession ||
                                                sending ||
                                                (!composerText.trim() &&
                                                    !composerFile)
                                            }
                                            onClick={handleSendSession}
                                        >
                                            {t("send")}
                                        </Button>
                                    </Box>
                                    {composerFile && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gridGap={8}
                                            marginTop={1}
                                        >
                                            <DescriptionIcon fontSize="small" />
                                            <Typography variant="caption">
                                                {composerFile.name}
                                            </Typography>
                                            <Tooltip
                                                title={t("removeAttachment")}
                                            >
                                                <IconButton
                                                    size="small"
                                                    aria-label={t(
                                                        "removeAttachment"
                                                    )}
                                                    onClick={() =>
                                                        setComposerFile(null)
                                                    }
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Divider style={{ margin: "12px 0" }} />
                                    <Box display="flex" gridGap={8}>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            placeholder={t("internalNote")}
                                            inputProps={{
                                                "aria-label": t("internalNote")
                                            }}
                                            value={noteText}
                                            onChange={(event) =>
                                                setNoteText(event.target.value)
                                            }
                                            fullWidth
                                        />
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddCommentIcon />}
                                            disabled={!actionContext}
                                            onClick={handleAddNote}
                                        >
                                            {t("note")}
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Box padding={3}>
                                <Alert severity="info">
                                    {t("selectConversationOrSearchCandidate")}
                                </Alert>
                            </Box>
                        )}
                    </Box>
                    {selectedApplicantId && (
                        <Box className={classes.sidePane}>
                            <Box className={classes.header}>
                                <Typography variant="subtitle1">
                                    {t("candidateProfile")}
                                </Typography>
                            </Box>
                            <Box className={classes.sideContent}>
                                <Box className={classes.profileHero}>
                                    <Box className={classes.profileHeroTop}>
                                        <Avatar
                                            className={classes.profileAvatar}
                                        >
                                            {getInitials(
                                                getDisplayText(
                                                    candidate.name,
                                                    language
                                                )
                                            )}
                                        </Avatar>
                                        <Box minWidth={0}>
                                            <Typography
                                                variant="h6"
                                                className={classes.profileName}
                                            >
                                                {getDisplayText(
                                                    candidate.name,
                                                    language
                                                ) || "-"}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className={classes.profileEmail}
                                            >
                                                {getWhatsappInboxProfileContactDisplay(
                                                    candidate.email,
                                                    candidate.mobileNumber,
                                                    language
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={classes.chips}>
                                        <Chip
                                            size="small"
                                            label={
                                                optedOut
                                                    ? t("optOut")
                                                    : optedIn
                                                    ? t("optIn")
                                                    : t("noOptIn")
                                            }
                                        />
                                        <Chip
                                            size="small"
                                            label={
                                                hasWindow
                                                    ? t("windowOpen")
                                                    : t("windowClosed")
                                            }
                                        />
                                    </Box>
                                </Box>

                                <Box className={classes.profileSection}>
                                    <WhatsappResolvedContactEpisodeControls
                                        mode={whatsappContactEpisodeMode}
                                        companyId={companyId}
                                        applicantId={selectedApplicantId}
                                        contexts={contactEpisodeActionContexts}
                                        selectedContext={
                                            activeContactEpisodeActionContext
                                        }
                                        onSelect={
                                            setSelectedContactEpisodeActionContext
                                        }
                                        onReassigned={async () => {
                                            await refreshConversation(
                                                selectedApplicantId,
                                                selectedSegmentId
                                            );
                                            await refreshConversations();
                                        }}
                                        notify={notify}
                                        t={t}
                                    />
                                </Box>

                                <Box className={classes.profileSection}>
                                    <Box
                                        className={classes.profileSectionHeader}
                                    >
                                        <Typography
                                            className={
                                                classes.profileSectionTitle
                                            }
                                        >
                                            {t("tags")}
                                        </Typography>
                                        <Tooltip title={t("addTag")}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={openAddTagEditor}
                                                    aria-label={t("addTag")}
                                                    disabled={
                                                        tagSaving ||
                                                        !actionContext
                                                    }
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                    <Box className={classes.profileTagRow}>
                                        {candidateTags.length === 0 && (
                                            <Typography
                                                variant="body2"
                                                className={classes.muted}
                                            >
                                                {t("noTags")}
                                            </Typography>
                                        )}
                                        {candidateTags.map((tag) => {
                                            const tagColor = getTagColor(tag);
                                            return (
                                                <Box
                                                    key={tag}
                                                    className={
                                                        classes.editableTagGroup
                                                    }
                                                >
                                                    <Chip
                                                        size="small"
                                                        icon={<LabelIcon />}
                                                        label={tag}
                                                        className={
                                                            classes.editableTag
                                                        }
                                                        disabled={
                                                            tagSaving ||
                                                            !actionContext
                                                        }
                                                        onDelete={() =>
                                                            removeCandidateTag(
                                                                tag
                                                            )
                                                        }
                                                        deleteIcon={
                                                            <CloseIcon fontSize="small" />
                                                        }
                                                        style={{
                                                            backgroundColor:
                                                                tagColor.background,
                                                            color:
                                                                tagColor.color
                                                        }}
                                                    />
                                                    <Tooltip
                                                        title={t("editTag", {
                                                            tag
                                                        })}
                                                    >
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    openEditTagEditor(
                                                                        tag
                                                                    )
                                                                }
                                                                aria-label={t(
                                                                    "editTagAria",
                                                                    { tag }
                                                                )}
                                                                disabled={
                                                                    tagSaving ||
                                                                    !actionContext
                                                                }
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    {tagEditorMode !== "closed" && (
                                        <Box className={classes.tagEditor}>
                                            <TextField
                                                autoFocus
                                                variant="outlined"
                                                size="small"
                                                placeholder={t("tagName")}
                                                inputProps={{
                                                    "aria-label": t("tagName")
                                                }}
                                                value={tagEditorValue}
                                                onChange={(event) =>
                                                    setTagEditorValue(
                                                        event.target.value
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        submitTagEditor();
                                                    }
                                                    if (
                                                        event.key === "Escape"
                                                    ) {
                                                        closeTagEditor();
                                                    }
                                                }}
                                                fullWidth
                                            />
                                            <Tooltip title={t("saveTag")}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={
                                                            submitTagEditor
                                                        }
                                                        aria-label={t(
                                                            "saveTag"
                                                        )}
                                                        disabled={
                                                            tagSaving ||
                                                            !actionContext
                                                        }
                                                    >
                                                        {tagSaving ? (
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                        ) : (
                                                            <CheckIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t("cancelTag")}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={closeTagEditor}
                                                        aria-label={t(
                                                            "cancelTag"
                                                        )}
                                                        disabled={tagSaving}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>

                                <Box className={classes.profileSection}>
                                    <Typography
                                        className={classes.profileSectionTitle}
                                    >
                                        {t("data")}
                                    </Typography>
                                    <Box className={classes.profileDetails}>
                                        <Box
                                            className={
                                                classes.profileDetailItem
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.profileDetailLabel
                                                }
                                            >
                                                {t("age")}
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.profileDetailValue
                                                }
                                            >
                                                {getDisplayText(
                                                    candidate.age,
                                                    language
                                                ) || "-"}
                                            </Typography>
                                        </Box>
                                        <Box
                                            className={
                                                classes.profileDetailItem
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.profileDetailLabel
                                                }
                                            >
                                                {t("cityState")}
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.profileDetailValue
                                                }
                                            >
                                                {getDisplayText(
                                                    candidate.city,
                                                    language
                                                ) || "-"}{" "}
                                                /{" "}
                                                {getDisplayText(
                                                    candidate.state,
                                                    language
                                                ) || "-"}
                                            </Typography>
                                        </Box>
                                        <Box
                                            className={
                                                classes.profileDetailItem
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.profileDetailLabel
                                                }
                                            >
                                                {t("createdAt")}
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.profileDetailValue
                                                }
                                            >
                                                {formatWhatsappInboxDate(
                                                    getDisplayText(
                                                        candidate.createdAt,
                                                        language
                                                    ),
                                                    language
                                                )}
                                            </Typography>
                                        </Box>
                                        <Box
                                            className={
                                                classes.profileDetailItem
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.profileDetailLabel
                                                }
                                            >
                                                {t("source")}
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.profileDetailValue
                                                }
                                            >
                                                {getDisplayText(
                                                    candidate.source,
                                                    language
                                                ) || "-"}
                                            </Typography>
                                        </Box>
                                        <Box
                                            className={
                                                classes.profileDetailItem
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.profileDetailLabel
                                                }
                                            >
                                                {t("updatedAt")}
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.profileDetailValue
                                                }
                                            >
                                                {formatWhatsappInboxDate(
                                                    getDisplayText(
                                                        candidate.updatedAt,
                                                        language
                                                    ),
                                                    language
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box className={classes.profileSection}>
                                    <Typography
                                        className={classes.profileSectionTitle}
                                    >
                                        {t("jobs")}
                                    </Typography>
                                    <Box className={classes.jobList}>
                                        {((candidate.jobs ||
                                            []) as Compleo.IObject[]).map(
                                            (job) => {
                                                const jobId = getDisplayText(
                                                    job.jobId,
                                                    language
                                                );
                                                return (
                                                    <Box
                                                        key={
                                                            jobId ||
                                                            getJobTitle(
                                                                job,
                                                                language
                                                            )
                                                        }
                                                        className={
                                                            classes.jobItem
                                                        }
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            className={
                                                                classes.jobTitle
                                                            }
                                                            noWrap
                                                        >
                                                            {getJobTitle(
                                                                job,
                                                                language
                                                            ) || "-"}
                                                        </Typography>
                                                        {jobId && (
                                                            <Chip
                                                                size="small"
                                                                label={jobId}
                                                                className={
                                                                    classes.jobCode
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            }
                                        )}
                                        {(!candidate.jobs ||
                                            (candidate.jobs as any[]).length ===
                                                0) && (
                                            <Typography
                                                variant="body2"
                                                className={classes.muted}
                                            >
                                                {t("noJobsFound")}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                <Box marginTop={2}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => setProfileOpen(true)}
                                        fullWidth
                                    >
                                        {t("viewFullProfile")}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Dialog
                open={contactDialogOpen}
                onClose={closeContactDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{t("startConversation")}</DialogTitle>
                <DialogContent>
                    <Box display="flex" gridGap={8}>
                        <TextField
                            autoFocus
                            variant="outlined"
                            size="small"
                            label={t("nameEmailOrPhone")}
                            value={contactSearch}
                            onChange={(event) => {
                                contactSearchRequestRef.current += 1;
                                setContactSearch(event.target.value);
                                setContactResults([]);
                                setContactSearchExecuted(false);
                                setContactLoading(false);
                            }}
                            fullWidth
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    handleSearchContacts();
                                }
                            }}
                        />
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={
                                contactSearch.trim().length < 4 ||
                                contactLoading
                            }
                            onClick={() => handleSearchContacts()}
                        >
                            {contactLoading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                t("search")
                            )}
                        </Button>
                    </Box>
                    <List className={classes.contactResults}>
                        {contactResults.map((result) => {
                            const isActionable =
                                result.status === "CAN_SEND_SESSION" ||
                                result.status === "CAN_START_WITH_TEMPLATE";
                            return (
                                <ListItem
                                    key={result.applicantId}
                                    button={isActionable}
                                    disabled={!isActionable}
                                    onClick={
                                        isActionable
                                            ? () => {
                                                  selectConversation(
                                                      {
                                                          applicantId: String(
                                                              result.applicantId
                                                          )
                                                      },
                                                      false
                                                  );
                                                  closeContactDialog();
                                              }
                                            : undefined
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            getFirstDisplayText(
                                                language,
                                                result.name,
                                                result.email
                                            ) || "-"
                                        }
                                        secondary={
                                            result.status ===
                                            "NO_VALID_WHATSAPP_NUMBER"
                                                ? t("candidateNoValidWhatsapp")
                                                : result.status === "OPTED_OUT"
                                                ? t("candidateOptOut")
                                                : result.status === "NO_OPT_IN"
                                                ? t(
                                                      "candidateNoOptInRegistered"
                                                  )
                                                : result.status ===
                                                  "CAN_START_WITH_TEMPLATE"
                                                ? t(
                                                      "candidateCanStartWithTemplate"
                                                  )
                                                : result.status ===
                                                  "NO_ACTIVE_WINDOW"
                                                ? t("candidateNoActiveWindow")
                                                : getDisplayText(
                                                      result.email,
                                                      language
                                                  )
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                        {contactLoading && contactResults.length === 0 && (
                            <ListItem>
                                <Typography className={classes.muted}>
                                    {t("searchingCandidates")}
                                </Typography>
                            </ListItem>
                        )}
                        {!contactLoading &&
                            contactSearchExecuted &&
                            contactResults.length === 0 && (
                                <ListItem>
                                    <Typography className={classes.muted}>
                                        {t("noCandidateFound")}
                                    </Typography>
                                </ListItem>
                            )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeContactDialog}>{t("close")}</Button>
                </DialogActions>
            </Dialog>

            <WhatsappInboxFilterDialog
                open={jobFilterOpen}
                loading={filterOptionsLoading}
                language={language}
                storeOptions={storeOptions}
                regionOptions={regionOptions}
                recruiterOptions={recruiterOptions}
                draftStores={draftStoreFilters}
                draftRegions={draftRegionFilters}
                draftRecruiters={draftRecruiterFilters}
                draftJobs={draftJobFilters}
                draftStartedByCurrentUser={draftStartedByCurrentUser}
                jobSearch={jobSearch}
                jobResults={jobResults}
                jobLoading={jobLoading}
                jobSearchExecuted={jobSearchExecuted}
                t={t}
                onClose={closeJobFilterDialog}
                onApply={applyJobFilters}
                onClear={clearDraftJobFilters}
                onStartedByCurrentUserChange={changeDraftStartedByCurrentUser}
                onToggleStore={toggleDraftStore}
                onToggleRegion={toggleDraftRegion}
                onToggleRecruiter={toggleDraftRecruiter}
                onToggleJob={toggleDraftJob}
                onJobSearchChange={changeJobSearch}
                onSearchJobs={handleSearchJobs}
            />

            <WhatsappTemplateSendDialog
                open={templateDialogOpen}
                companyId={companyId}
                applicantId={selectedApplicantId}
                segmentId={
                    getWhatsappInboxActionTarget(
                        selectedApplicantId,
                        actionContext
                    ).segmentId
                }
                contactEpisodeActionContext={
                    activeContactEpisodeActionContext
                }
                conversationScope="APPLICANT_TIMELINE"
                language={language}
                jobOptions={selectedCandidateJobOptions}
                onClose={() => setTemplateDialogOpen(false)}
                onSent={handleTemplateSent}
                showMessage={notify}
            />

            <ApplicantViewModalContainer
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
                ApplicantId={selectedApplicantId}
                loadingData={false}
                module="APPLICANTVIEW"
            />
        </Box>
    );
};

const WhatsappInboxWithScopes = () => {
    const [t, i18n] = useTranslation(moduleName, { useSuspense: false });
    const { company } = useAuthState();
    const { pendingCount: summaryPendingCount } = useWhatsappUnread();
    const [pendingCount, setPendingCount] = React.useState(
        Number(summaryPendingCount || 0)
    );
    const companyId = Number(company?.id || company?.companyId);
    const realtimeRevision = useWhatsappInboxRealtimeRevision({
        companyId,
        enabled: company?.whatsappEnabled === true
    });
    const mode = normalizeWhatsappContactEpisodeMode(
        company?.whatsappContactEpisodeMode
    );
    const language = normalizeWhatsappInboxLanguage(
        i18n.languages?.[0] || i18n.language
    );

    React.useEffect(() => {
        setPendingCount(Number(summaryPendingCount || 0));
    }, [summaryPendingCount]);

    if (!shouldShowWhatsappContactEpisodeInbox(mode)) {
        return <WhatsappInboxContent realtimeRevision={realtimeRevision} />;
    }

    return (
        <Box
            style={{
                height: "calc(100vh - 88px)",
                padding: 16,
                backgroundColor: "#f4f6f8"
            }}
        >
            <WhatsappInboxScopeTabs
                mode={mode}
                pendingCount={pendingCount}
                t={t}
                normalInbox={
                    <WhatsappInboxContent
                        embedded
                        realtimeRevision={realtimeRevision}
                    />
                }
                pendingInbox={
                    <WhatsappContactEpisodePending
                        companyId={companyId}
                        mode={mode === "ACTIVE" ? "ACTIVE" : "READ_ONLY"}
                        t={t}
                        language={language}
                        onPendingCountChange={setPendingCount}
                        realtimeRevision={realtimeRevision}
                    />
                }
            />
        </Box>
    );
};

const WhatsappInbox = () => (
    <Page featureName="" featureMainDescription="" maxWidth={false}>
        <WhatsappInboxWithScopes />
    </Page>
);

export default WhatsappInbox;
